import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, DestroyRef, effect, ElementRef, HostListener, inject, input, OnDestroy, OnInit, output, signal, untracked, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { IconComponent, IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent, MenuItemType } from 'src/app/@core/components/ui/menu/menu.component';
import { MOBILE_BREAKPOINT } from 'src/app/@core/constants/breakpoints';
import { FILE_TYPE } from 'src/app/@core/models/enums';
import { UI_FEATURE } from 'src/app/@core/models/feature-mapping';
import { IKnowledgeMapping, LINKED_DOCUMENT_TYPE, IUser, ResponseJson } from 'src/app/@core/models/model';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { BrowserExtensionService } from 'src/app/@core/services/browser-extension.service';
import { FeatureFlagService } from 'src/app/@core/services/feature-flag.service';
import { SOCKET_EVENTS, SocketService } from 'src/app/@core/services/socket.service';
import { SubscriptionStateService } from 'src/app/@core/services/subscription-state.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { FileDropTypes } from 'src/app/@shared/components/file-drop/file-drop.types';
import { FileSelectionComponent } from 'src/app/@shared/components/file-selection/file-selection.component';
import { DropdownOption, FetchFunction, FileItem, FileSelectionChangeEvent, FileSelectionConfig, FileSelectionSubmitEvent, TabType } from 'src/app/@shared/components/file-selection/file-selection.model';
import { KeyboardShortcutConfig } from 'src/app/@shared/models/keyboard-shortcut.models';
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';
import { NumberAbbreviationPipe } from 'src/app/@shared/pipes/number-abbreviation.pipe';
import { KeyboardShortcutService } from 'src/app/@shared/services/keyboard-shortcut.service';
import { VoiceRecognitionService } from 'src/app/@shared/services/voice-recognition.service';
import { getFileType } from 'src/app/@shared/utils/utils';
import { createMutationHook } from 'src/app/angular-query/use-query';
import { CREDITS_SVG, GENERAL_AGENT_SVG } from 'src/app/common/svgs';
import { CONTRACT_TYPE, DOCUMENT_MODEL, EMBEDDINGS_GENERATION_STATUS, KNOWLEDGE_SCOPE } from 'src/app/contract-management/models/contract-management.model';
import { ContractService } from 'src/app/contract-management/services/contracts.service';
import { IntegrationsService } from 'src/app/contract-management/services/integrations.service';
import { KnowledgeMappingService } from 'src/app/contract-management/services/knowledge-mapping.service';
import { KnowledgeBaseService } from 'src/app/contract-management/services/knowledgebase.service';
import { LinkDocumentService } from 'src/app/contract-management/services/link-document.service';
import { ProjectService } from 'src/app/contract-management/services/project.service';
import { RepositoryService } from 'src/app/contract-management/services/repository.service';
import { UserCreditsStateService } from 'src/app/features/credits/services/user-credits-state.service';
import { AGENT_FEATURES } from 'src/app/pricing/models';
import { AttachmentGroupComponent } from '../../../../../@shared/components/attachment-group/attachment-group.component';
import { VoiceRecordingUIComponent } from '../../../../../@shared/components/voice-recording-ui/voice-recording-ui.component';
import { CONNECTOR_PROVIDER_INFO, DATABASE_PROVIDERS, MISC_CONNECTORS, TOOLKIT_PROVIDERS } from '../../../constants/tool.constants';
import { IThread, IThreadMetadata, ITokenLimitsAgent, LLM_PROVIDERS, THREAD_METADATA_STATUS } from '../../../models/model';
import { AGENT_MODE, CHAT_STYLES, ChatAttachment, ChatMessagePayload, CREDIT_NUDGE_MODE, QueryOptions, RestoredInputState } from '../../../models/types';
import { GeneralAgentService, IAttachedFile } from '../../../services/general-agent.service';
import { ThreadMessagesService } from '../../../services/thread-messages.service';
import { isFeatureEnabled as checkFeatureEnabled, THREAD_AGENT_CONFIG, THREAD_AGENT_FEATURES } from '../../../tokens/thread-agent-config.token';
import { AIModelProviderOption, AVAILABLE_MODELS, ModelSelectorComponent } from './model-selector/model-selector.component';

import { SelectButtonComponent } from 'src/app/@core/components/ui/select-button/select-button.component';

const ALL_KNOWLEDGE_FOLDER_PREFIX = 'all-knowledge-';

/**
 * ThreadInputComponent
 * 
 * Clean, focused input component for the General Agent chat interface.
 * 
 * Responsibilities:
 * - Text input with auto-grow textarea
 * - Send/Stop message actions
 * - AI model selection
 * - Options menu (attachments, KB, projects)
 * - Tools menu (styles, web search, canvas, custom instructions)
 * - Connectors menu (integrations)
 * - Loading states (sending, processing)
 * - Keyboard shortcuts (Enter to send, Shift+Enter for new line)
 * - Voice input integration
 * 
 * Design principles:
 * - Single Responsibility: Focus on message composition
 * - Composition over Inheritance: Use child components for complex features
 * - Signals for Reactivity: Modern Angular signal-based state
 * - Tailwind for Styling: No custom CSS unless necessary
 */
@Component({
  selector: 'app-thread-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TextareaModule,
    TooltipModule,
    DialogComponent,
    ButtonComponent,
    IconComponent,
    ModelSelectorComponent,
    MenuComponent,
    FileSelectionComponent,
    AttachmentGroupComponent,
    AssetUrlPipe,
    SelectButtonComponent,
    VoiceRecordingUIComponent,
  ],
  templateUrl: './thread-input.component.html',
  styleUrl: './thread-input.component.scss',
  animations: [
    trigger('fadeOverlay', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('150ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class ThreadInputComponent implements OnInit, OnDestroy, AfterViewInit {

  // ============================================
  // View References
  // ============================================
  @ViewChild('messageTextarea') messageTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('optionsMenu') optionsMenu!: MenuComponent;
  @ViewChild('toolsMenu') toolsMenu!: MenuComponent;
  @ViewChild('connectorsMenu') connectorsMenu!: MenuComponent;
  @ViewChild('mobileOverflowMenu') mobileOverflowMenu!: MenuComponent;
  @ViewChild('fileSelectionDialog') fileSelectionDialogRef?: FileSelectionComponent;

  // ============================================
  // Inputs
  // ============================================
  provider = input<LLM_PROVIDERS>(LLM_PROVIDERS.ANTHROPIC);
  threadId = input<string>('');
  threadMetadataId = input<string>('');
  needTokenLimits = input<boolean>(false);
  hasThreadSizeError = input<boolean>(false);
  showEmptyState = input<boolean>(false); // Show Bob's greeting for new chats
  creationLoading = input<boolean>(false); // Thread creation loading state
  threadContextReady = input<boolean>(true);
  embeddingsStatus = input<EMBEDDINGS_GENERATION_STATUS | null>(EMBEDDINGS_GENERATION_STATUS.COMPLETED);
  documentId = input<string>('');
  documentTypeId = input<string>('');
  projectId = input<string>('');
  restoredCustomInstructions = input<string | null>(null);
  restoredInputState = input<RestoredInputState | null>(null);
  /** Set false in parent components that render their own drag overlay (e.g. active-thread). */
  renderDragOverlay = input<boolean>(true);
  compareId = input<string>('');
  /** Compare documents for compare chat context (populates COMPARE_DOCS tool sub-options). */
  compareDocuments = input<{ _id: string; name: string }[]>([]);
  /** KPI documents for compare chat context (populates KPIs tool sub-options). */
  kpiDocuments = input<{ _id: string; name: string }[]>([]);
  /** Form questions for document-form chat context (populates FORM_QUESTIONS tool sub-options). */
  formQuestions = input<{ _id: string; name: string; sectionTitle?: string }[]>([]);
  /** When set, auto-selects the matching form question in the tools map. */
  selectedFormQuestionId = input<string | null>(null);

  // ============================================
  // Outputs
  // ============================================
  sendMessageEvent = output<ChatMessagePayload>();
  pauseThreadEvent = output<void>();
  selectedOptionsChange = output<CustomMenuItem[]>();
  selectedToolsOptionsChange = output<CustomMenuItem[]>();
  selectedConnectorsChange = output<CustomMenuItem[]>();
  newThreadCreatedFromUpload = output<{ thread: IThread; threadMetadata: IThreadMetadata[]; currentThreadMetadata: IThreadMetadata; inputState: RestoredInputState }>();
  modeChange = output<AGENT_MODE>();
  /** Emitted whenever the composer text changes — used by parent for soft-nudge debouncing. */
  queryChange = output<string>();
  /** Emitted with settled (non-temp) attachments on every change — parent stores these to survive component recreation on resize. */
  attachmentsChange = output<ChatAttachment[]>();
  /** Emitted when a file drag enters/leaves the window — lets parents that own their own overlay react. */
  windowDragActiveChange = output<boolean>();

  private agentConfig = inject(THREAD_AGENT_CONFIG);
  isFeatureEnabled = (feature: THREAD_AGENT_FEATURES) => checkFeatureEnabled(this.agentConfig, feature);
  private userCreditsState = inject(UserCreditsStateService);
  THREAD_AGENT_FEATURES = THREAD_AGENT_FEATURES;

  // ============================================
  // State - Message Input
  // ============================================
  messageText = signal<string>('');

  // ============================================
  // State - Mode
  // ============================================
  selectedMode = signal<AGENT_MODE>(AGENT_MODE.ASK);
  readonly AGENT_MODE = AGENT_MODE;
  readonly CREDIT_NUDGE_MODE = CREDIT_NUDGE_MODE;
  CREDITS_SVG = CREDITS_SVG;

  availableModes = computed(() => {
    const settings = this.currentUser()?.agentModeSettings;
    const askEnabled = settings ? settings.askMode : true;
    const agentEnabled = settings ? settings.agentMode : true;
    const modes = [];
    if (askEnabled) modes.push({ label: 'Ask mode', value: AGENT_MODE.ASK, icon: 'zap', iconSource: 'lucide' as const });
    if (agentEnabled) modes.push({ label: 'Agentic mode', value: AGENT_MODE.AGENTIC, icon: CREDITS_SVG.AGENT_MODE, iconSource: 'svg' as const, iconClass: 'text-violet-500' });
    return modes;
  });

  showModeTabs = computed(() => this.availableModes().length > 0);

  defaultModeEffect = effect(() => {
    const modes = this.availableModes();
    const current = this.selectedMode();
    if (modes.length === 0) {
      this.router.navigate(['/explore']);
      return;
    }
    const isCurrentAvailable = modes.some(m => m.value === current);
    if (!isCurrentAvailable) {
      this.selectedMode.set(modes[0].value);
    }
  });

  private modeChangeEffect = effect(() => {
    this.modeChange.emit(this.selectedMode());
  });

  // ============================================
  // State - Agent Mode Nudge
  // ============================================
  private nudgeDismissed = signal<boolean>(false);

  largeFileAttached = signal<boolean>(false);

  readonly agentNudgeTrigger = computed(() => {
    if (this.selectedMode() !== AGENT_MODE.ASK) return false;
    if (this.nudgeDismissed()) return false;

    const atts = this.attachments();
    const text = this.messageText().toLowerCase();

    // Trigger 0: Large file attached (>5MB)
    if (this.largeFileAttached()) return true;

    // Trigger 1: Multiple documents (2+)
    if (atts.length >= 2) return true;

    // Trigger 2: Generation keywords
    const generationPattern = /\b(generate|generates|generated|generating|generation|create|build|draft|extract all|list all|convert this to|make me a|compare these|find differences|build a matrix|write a)\b/i;
    if (generationPattern.test(text)) return true;

    // Trigger 3: Output format requests
    const outputPattern = /\bas ppt\b|\bas excel\b|\bas word\b|\bas pdf\b|\bin table format\b|\bas a spreadsheet\b|\bas a presentation\b|\bdownloadable\b|\bexportable\b/i;
    if (outputPattern.test(text)) return true;

    // Trigger 4: Multi-step task indicators
    const multiStepPattern = /\band then\b|\bafter that\b|\bstep by step\b/i;
    if (multiStepPattern.test(text)) return true;

    // Trigger 4 (cont): 3+ questions in one message
    const questionCount = (text.match(/\?/g) || []).length;
    if (questionCount >= 3) return true;

    return false;
  });

  // Debounced visibility: delays show by 300ms to avoid flicker on fast typing,
  // but hides immediately when trigger clears (e.g. dismiss, mode switch).
  agentNudgeVisible = signal<boolean>(false);
  private nudgeDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  agentModeHasCredits = computed(() => this.userCreditsState.agentRemaining() > 0);

  // ============================================
  // State - Credit Nudge
  // ============================================
  creditNudgeMode = signal<CREDIT_NUDGE_MODE | null>(null);


  // ============================================
  // State - User (for empty state greeting)
  // ============================================
  currentUser = signal<IUser | null>(null);

  // Computed: Dynamic greeting based on time of day and day of week
  greeting = computed(() => {
    const now = new Date();
    const hour = now.getHours();
    const day = now.getDay(); // 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat
    const dateKey = now.toDateString();

    // Stable per-day random index seeded by date string
    const seed = dateKey.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    const pick = (arr: string[]) => arr[seed % arr.length];

    // Day-specific overrides take priority
    if (day === 5) return pick(['Happy Friday!', 'TGIF', 'Happy Friday']);
    if (day === 6) return pick(['Happy Saturday', 'Hope you\'re enjoying the weekend']);
    if (day === 0) return pick(['Happy Sunday', 'Enjoy the rest of your weekend']);
    if (day === 1) {
      if (hour < 12) return pick(['Happy Monday', 'Hope your week is off to a great start']);
    }

    // Time-of-day pools
    if (hour < 6) return pick(['Burning the midnight oil?', 'Up early!', 'Late night, huh?']);
    if (hour < 12) return pick(['Good morning', 'Morning', 'Rise and shine']);
    if (hour < 14) return pick(['Good afternoon', 'Hope your morning went well', 'Hey there']);
    if (hour < 18) return pick(['Good afternoon', 'Afternoon', 'Hope your day is going well']);
    if (hour < 21) return pick(['Good evening', 'Evening', 'Hey there']);
    return pick(['Good evening', 'Winding down?', 'Still at it?']);
  });

  // Computed: Rotating Bob-themed subtitle seeded by date
  subtitle = computed(() => {
    const dateKey = new Date().toDateString();
    const seed = dateKey.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    const subtitles = [
      "I'm Bob, how can I help?",
      "Bob here, what are we working on?",
      "I'm Bob, let's get things done!",
      "Bob at your service, ask me anything.",
      "I'm Bob, ready when you are.",
    ];
    return subtitles[seed % subtitles.length];
  });

  // ============================================
  // State - AI Model
  // ============================================
  modelOptions = signal<AIModelProviderOption[]>(AVAILABLE_MODELS);
  selectedModel = signal<AIModelProviderOption | null>(null);
  selectedProviderSignal = signal<LLM_PROVIDERS>(LLM_PROVIDERS.ANTHROPIC);

  // ============================================
  // State - Loading & Disabled
  // ============================================
  isLoadingOrSending = computed(() => {
    const status = this.threadMessagesService.threadMetadataStatus();
    return this.creationLoading() ||
      status === THREAD_METADATA_STATUS.PENDING ||
      status === THREAD_METADATA_STATUS.IN_PROGRESS;
  });

  // Computed: Check if input should be disabled
  disabled = computed(() => {
    return this.hasThreadSizeError() || this.creationLoading() || !this.threadContextReady();
  });

  disableSend = computed(() => {
    return this.disabled() || !this.messageText().trim() || this.disableInAskMode();
  });

  disableInAskMode = computed(() =>
    this.selectedMode() === AGENT_MODE.ASK && (this.hasUploadingFiles() || this.embeddingsStatus() !== EMBEDDINGS_GENERATION_STATUS.COMPLETED)
  );

  sendButtonTooltip = computed(() => {
    if (this.disableInAskMode()) {
      const status = this.embeddingsStatus();
      if (status === EMBEDDINGS_GENERATION_STATUS.IN_PROGRESS) return 'Document embedding is being generated...';
      if (status === EMBEDDINGS_GENERATION_STATUS.FAILED) return 'Embedding failed — regenerate to submit';
      if (status === null) return 'Generate document embedding to submit';
      if (this.hasUploadingFiles()) return 'Attachment is still processing...';
    }
    if (!this.disabled()) return '';
    if (!this.threadContextReady()) return 'Loading thread...';
    return 'Chat limit exceeded';
  });

  // ============================================
  // State - Custom Instructions
  // ============================================
  customInstructions = signal<string>('');
  openCustomInstructionsDialog = signal<boolean>(false);
  private previousThreadId = '';
  protected _instructionsBeforeEdit = '';

  // ============================================
  // State - UI Interactions
  // ============================================
  hoveredOption: CustomMenuItem | null = null;

  // ============================================
  // State - Mobile Responsiveness
  // ============================================
  readonly MOBILE_BREAKPOINT = MOBILE_BREAKPOINT;
  currentWindowWidth = signal<number>(typeof window !== 'undefined' ? window.innerWidth : 1200);

  @HostListener('window:resize')
  onResize() {
    this.currentWindowWidth.set(window.innerWidth);
  }

  @HostListener('window:pagehide')
  onPageHide(): void {
    const threadId = this.threadId();
    if (threadId) {
      this.generalAgentService.saveDraftOnUnload(threadId, this.messageText());
    }
  }

  isMobile = computed(() => this.currentWindowWidth() < this.MOBILE_BREAKPOINT);

  // Mobile overflow menu: Tools, Connectors, Voice, Model Selector
  mobileOverflowMenuItems = computed<CustomMenuItem[]>(() => {
    const toolSubOptions: CustomMenuItem[] = this.filteredToolsMenuItems().map(tool => ({
      ...tool,
      subOptions: tool.subOptions?.map(sub => ({
        ...sub,
        callback: (item: CustomMenuItem) => this.onToolsOptionSelect(item)
      })),
      callback: tool.subOptions && tool.subOptions.length > 0
        ? undefined
        : (item: CustomMenuItem) => this.onToolsOptionSelect(item)
    }));

    const items: CustomMenuItem[] = [
      {
        label: 'Tools',
        icon: 'settings-2',
        iconSource: IconSourceEnum.LUCIDE,
        subOptions: toolSubOptions
      },
    ];

    if (this.isFeatureEnabled(THREAD_AGENT_FEATURES.CONNECTORS) && this.hasIntegrationsFeature() && this.connectorsMenuItems().length > 0 && this.selectedMode() == AGENT_MODE.AGENTIC) {
      items.push({
        label: 'Connectors',
        icon: 'blocks',
        iconSource: IconSourceEnum.LUCIDE,
        subOptions: this.connectorsMenuItems().map(c => ({
          ...c,
          callback: (item: CustomMenuItem) => this.onConnectorsOptionSelect(item)
        }))
      });
    }

    if (this.voiceService.isSupported()) {
      items.push({
        label: 'Voice input',
        icon: 'mic',
        iconSource: IconSourceEnum.LUCIDE,
        callback: () => this.startVoiceRecording()
      });
    }

    const selectedProviderValue = this.selectedProviderSignal();
    items.push({
      label: 'Model',
      icon: 'cpu',
      iconSource: IconSourceEnum.LUCIDE,
      subOptions: this.modelOptions().map(m => ({
        label: m.label,
        value: m.value,
        icon: m.icon || 'cpu',
        iconSource: m.icon ? IconSourceEnum.SVG : IconSourceEnum.LUCIDE,
        class: selectedProviderValue === m.value ? 'font-semibold text-primary' : '',
        callback: () => this.onModelChange(m)
      }))
    });

    return items;
  });

  onMobileOverflowSelect(_item: CustomMenuItem): void {
    // Individual item callbacks handle actions
  }

  // ============================================
  // State - Attachments
  // ============================================
  attachments = signal<ChatAttachment[]>([]);
  uploadedAttachments = signal<IAttachedFile[]>([]);
  filesInProgress = signal<boolean>(false);
  attachFilesLoading = signal<boolean>(false);
  hasUploadingFiles = signal<boolean>(false);
  private doneClicked = false;
  // FileItems selected in the file dialog (used for pre-selection & removals) — derived from attachments so the two never drift.
  attachmentFileItems = computed<FileItem[]>(() => this.attachments().map(att => this.chatAttachmentToFileItem(att)));
  // Staged files dropped in new-chat context
  private stagedFilesMap = new Map<string, File>();
  // True while the first new-chat upload is creating the thread. Further files
  // added during this window are queued (stagedFilesMap) instead of spawning a
  // second thread, then flushed to the new thread via restoredInputState.
  private creatingThreadFromUpload = false;
  // One-shot guard so restoredInputState.stagedFiles are uploaded only once.
  private restoredStagedFilesUploaded = false;

  private pendingSendPayload: {
    query: string;
    mode: AGENT_MODE;
    provider: LLM_PROVIDERS;
    tools: any[];
    connectors: any[];
  } | null = null;

  private deferredSendPayload: ChatMessagePayload | null = null;

  // Thread data from file uploads
  thread = signal<IThread | null>(null);
  uploadedThreadId = signal<string>('');
  threadMetadata = signal<IThreadMetadata[]>([]);
  currentThreadMetadata = signal<IThreadMetadata | null>(null);

  readonly EMBEDDINGS_GENERATION_STATUS = EMBEDDINGS_GENERATION_STATUS;

  // ============================================
  // Attachment Mutations
  // ============================================
  attachFilesMutation = createMutationHook<FormData, any>(
    (formData: FormData) => {
      this.attachFilesLoading.set(true);
      return this.generalAgentService.attachFiles(formData);
    },
    (res: ResponseJson<{ thread: IThread; threadMetadata: IThreadMetadata[]; currentThreadMetadata: IThreadMetadata; files: IAttachedFile[]; failedFiles?: { name: string; error: string }[]; isNewThread?: boolean }>, variables: FormData) => {
      this.attachFilesLoading.set(false);
      this.creatingThreadFromUpload = false;
      const batchTempIds = new Set<string>(JSON.parse(variables.get('_tempIds') as string ?? '[]'));
      if (res.success) {
        const isNewThread = res.data?.isNewThread ?? false;

        // Save thread data and metadata
        if (res.data?.thread) {
          this.thread.set(res.data.thread);
          this.uploadedThreadId.set(res.data.thread._id);
        }
        if (res.data?.threadMetadata && res.data.threadMetadata.length > 0) {
          this.threadMetadata.set(res.data.threadMetadata);
        }
        if (res.data?.currentThreadMetadata) {
          this.currentThreadMetadata.set(res.data.currentThreadMetadata);
        }

        // Emit event so parent can update sidebar cache and navigate
        if (isNewThread && res.data?.thread && res.data?.currentThreadMetadata) {
          if (this.pendingSendPayload) {
            const saved = this.pendingSendPayload;
            this.pendingSendPayload = null;
            const uploadedAttachments = res.data.files.map((attachment: IAttachedFile) => ({
              name: attachment.name || '',
              type: getFileType(attachment.name) || attachment.type || '',
              _id: attachment._id || '',
              docModel: attachment.docModel,
              refDocModel: attachment.refDocModel,
              attachmentData: attachment.attachmentData
            }));
            const payload: ChatMessagePayload = {
              query: saved.query,
              provider: saved.provider,
              mode: saved.mode,
              attachments: uploadedAttachments,
              tools: saved.tools,
              connectors: saved.connectors,
              threadId: res.data.thread._id,
              threadMetadataId: res.data.currentThreadMetadata._id
            };

            const inProgressFiles = res.data.files.filter(
              (f: IAttachedFile) => f.status === EMBEDDINGS_GENERATION_STATUS.IN_PROGRESS
            );
            if (inProgressFiles.length > 0) {
              this.uploadedAttachments.update(current => [...current, ...inProgressFiles]);
              this.filesInProgress.set(true);
              this.updateUploadingStatus();
              this.deferredSendPayload = payload;
              return;
            }

            this.sendMessageEvent.emit(payload);
            this.clearInput();
            return;
          } else {
            // Flush any files that were queued while this thread was being
            // created. Carry the raw blobs to the active thread (File objects
            // survive history.state structured-clone) and strip their staged
            // temp entries so they don't show as duplicates — they will be
            // re-added properly when uploaded to the new thread.
            const queuedStagedIds = new Set(this.stagedFilesMap.keys());
            const queuedStagedFiles = Array.from(this.stagedFilesMap.values());
            this.stagedFilesMap.clear();
            if (queuedStagedIds.size > 0) {
              this.attachments.update(a => a.filter(att => !queuedStagedIds.has(att._id)));
            }
            this.newThreadCreatedFromUpload.emit({
              thread: res.data.thread,
              threadMetadata: res.data.threadMetadata || [],
              currentThreadMetadata: res.data.currentThreadMetadata,
              inputState: {
                query: this.messageText(),
                provider: this.selectedProviderSignal(),
                mode: this.selectedMode(),
                attachments: res.data.files.map((attachment: IAttachedFile) => ({
                  _id: attachment._id,
                  docModel: attachment.docModel,
                  refDocModel: attachment.refDocModel,
                  name: attachment.name,
                  type: getFileType(attachment.name) || attachment.type,
                  attachmentData: attachment.attachmentData,
                  metadata: attachment.metadata
                })),
                largeFileAttached: this.largeFileAttached(),
                stagedFiles: queuedStagedFiles.length > 0 ? queuedStagedFiles : null
              }
            });
          }
        }

        // Remove only this batch's temp attachments so concurrent uploads don't wipe each other.
        this.attachments.update(attachments => {
          const filtered = attachments.filter(a => !batchTempIds.has(a._id));
          const newAttachments = res.data.files.map((attachment: IAttachedFile) => ({
            _id: attachment._id,
            docModel: attachment.docModel,
            refDocModel: attachment.refDocModel,
            name: attachment.name,
            type: getFileType(attachment.name) || attachment.type,
            status: attachment.status,
            attachmentData: attachment.attachmentData,
            metadata: attachment.metadata
          }));
          return [...filtered, ...newAttachments];
        });

        // Update button state
        this.updateUploadingStatus();

        // Track in-progress files so socket events can update them and filesInProgress correctly
        const inProgressFiles = res.data.files.filter((f: IAttachedFile) => f.status === EMBEDDINGS_GENERATION_STATUS.IN_PROGRESS);
        if (inProgressFiles.length > 0) {
          this.uploadedAttachments.update(current => [...current, ...inProgressFiles]);
        }

        // Only close dialog on successful API response
        this.openFileSelectionDialog.set(false);
        if (this.uploadedAttachments().every(uploadedAttachment => uploadedAttachment.status === EMBEDDINGS_GENERATION_STATUS.COMPLETED)) {
          this.filesInProgress.set(false);
        }
        this.doneClicked = false;

        res.data?.failedFiles?.forEach((failed) => {
          this.toastService.error(`${failed.name} has failed to upload`);
        });
      } else {
        // Close the file selection dialog
        this.openFileSelectionDialog.set(false);
        this.doneClicked = false;
        // Clear only this batch's temp attachments on error
        this.attachments.update(a => a.filter(att => !batchTempIds.has(att._id)));
        this.filesInProgress.set(false);
        this.updateUploadingStatus();
      }
    },
    (_error: unknown, variables: FormData) => {
      const batchTempIds = new Set<string>(JSON.parse(variables.get('_tempIds') as string ?? '[]'));
      this.attachFilesLoading.set(false);
      this.creatingThreadFromUpload = false;
      this.toastService.error('Selected file(s) upload failed');
      this.openFileSelectionDialog.set(false);
      this.doneClicked = false;
      this.attachments.update(a => a.filter(att => !batchTempIds.has(att._id)));
      this.filesInProgress.set(false);
      this.updateUploadingStatus();
    }
  );

  deleteUploadedFileMutation = createMutationHook<{ fileId: string, threadMetadataId: string }, {}>(
    ({ fileId, threadMetadataId }) => {
      return this.generalAgentService.deleteUploadedFile(fileId, threadMetadataId);
    },
    (res: any) => {
      if (res.success) {

      }
    }
  );

  isWindowDragActive = signal<boolean>(false);
  private dragOverTimer: ReturnType<typeof setTimeout> | null = null;

  private setWindowDragActive(active: boolean): void {
    if (this.isWindowDragActive() === active) return;
    this.isWindowDragActive.set(active);
    this.windowDragActiveChange.emit(active);
  }

  @HostListener('window:dragover', ['$event'])
  onWindowDragOver(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    this.setWindowDragActive(true);
    // Heartbeat: if dragover stops firing for 100ms the drag left the window.
    if (this.dragOverTimer) clearTimeout(this.dragOverTimer);
    this.dragOverTimer = setTimeout(() => {
      this.dragOverTimer = null;
      this.setWindowDragActive(false);
    }, 100);
  }

  @HostListener('window:drop', ['$event'])
  onWindowDrop(event: DragEvent): void {
    const wasActive = this.isWindowDragActive();
    if (this.dragOverTimer) { clearTimeout(this.dragOverTimer); this.dragOverTimer = null; }
    this.setWindowDragActive(false);
    if (!wasActive) return; // inner drop zone handled it via stopPropagation
    event.preventDefault();
    if (this.disabled()) return;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      if (!this.threadId() && !this.threadMetadataId()) {
        this.handleNewChatLocalFiles(fileArray);
      } else {
        this.uploadLocalFiles(fileArray);
      }
    }
  }


  // ============================================
  // State - Voice Input
  // ============================================
  private tempTranscript = signal<string>('');
  isVoiceStarting = signal<boolean>(false);
  isConfirmingVoice = signal<boolean>(false);

  // ============================================
  // Computed - Token Usage
  // ============================================
  maxTokens = computed(() => {
    const model = this.selectedModel();
    const tokenLimits: ITokenLimitsAgent = this.threadMessagesService.tokenLimitsAgent();
    if (!model || !tokenLimits) {
      return 0;
    }
    const provider: LLM_PROVIDERS = model.value;
    if (!provider) {
      return 0;
    }
    return tokenLimits[provider as keyof ITokenLimitsAgent].max_tokens || 0;
  });

  totalTokens = computed(() => {
    const inputTokens = this.threadMessagesService.inputTokens();
    if (inputTokens !== null && inputTokens !== undefined) {
      return inputTokens;
    }
    return 0;
  });

  tokenUsagePercentage = computed(() => {
    const total = this.totalTokens();
    const max = this.maxTokens();
    if (max === 0) {
      return 0;
    }
    return Math.min((total / max) * 100, 100);
  });

  // ============================================
  // State - Menu Items & Selections
  // ============================================
  readonly SVG_ICONS = GENERAL_AGENT_SVG;
  readonly QueryOptions = QueryOptions;
  readonly CHAT_STYLES = CHAT_STYLES;

  // Options Menu (Attachments)
  isOptionsMenuOpen = signal<boolean>(false);
  optionsMenuItems = signal<CustomMenuItem[]>([]);
  selectedOptions = signal<CustomMenuItem[]>([]);

  // Tools Menu (Styles, Web Search, Canvas, etc.)
  toolsMenuItems = signal<CustomMenuItem[]>([
    {
      label: 'Use style',
      value: QueryOptions.USE_STYLES,
      icon: this.SVG_ICONS.USE_STYLE,
      iconSource: IconSourceEnum.SVG,
      type: MenuItemType.SINGLE_SELECT,
      subOptions: [
        {
          label: CHAT_STYLES.NORMAL,
          icon: this.SVG_ICONS.NORMAL_STYLE,
          iconSource: IconSourceEnum.SVG,
          value: CHAT_STYLES.NORMAL,
        },
        {
          label: CHAT_STYLES.LEARNING,
          icon: this.SVG_ICONS.LEARNING_STYLE,
          iconSource: IconSourceEnum.SVG,
          value: CHAT_STYLES.LEARNING,
        },
        {
          label: CHAT_STYLES.CONCISE,
          icon: this.SVG_ICONS.CONCISE_STYLE,
          iconSource: IconSourceEnum.SVG,
          value: CHAT_STYLES.CONCISE,
        },
        {
          label: CHAT_STYLES.FORMAL,
          icon: this.SVG_ICONS.FORMAL_STYLE,
          iconSource: IconSourceEnum.SVG,
          value: CHAT_STYLES.FORMAL,
        },
        {
          label: CHAT_STYLES.CASUAL,
          icon: this.SVG_ICONS.CASUAL_STYLE,
          iconSource: IconSourceEnum.SVG,
          value: CHAT_STYLES.CASUAL,
        },
        {
          label: CHAT_STYLES.EXPLANATORY,
          icon: this.SVG_ICONS.EXPLANATORY_STYLE,
          iconSource: IconSourceEnum.SVG,
          value: CHAT_STYLES.EXPLANATORY,
        },
      ],
    },
    {
      label: 'Web search',
      value: QueryOptions.WEB_SEARCH,
      icon: 'globe',
      iconSource: IconSourceEnum.LUCIDE,
      subOptions: [],
      type: MenuItemType.TOGGLE,
      toggleState: false,
    },
    {
      label: 'Canvas',
      value: QueryOptions.CANVAS,
      icon: this.SVG_ICONS.CANVAS,
      iconSource: IconSourceEnum.SVG,
      subOptions: [],
      type: MenuItemType.TOGGLE,
      toggleState: false,
    },
    {
      label: 'Custom instructions',
      value: QueryOptions.CUSTOM_INSTRUCTIONS,
      icon: this.SVG_ICONS.INSTRUCTIONS,
      iconSource: IconSourceEnum.SVG,
      subOptions: [],
    }
  ]);

  /** Map each tool (QueryOptions) to its feature key. Tools not in the map are shown when config is absent. */
  private static readonly TOOL_FEATURE_KEYS: Partial<Record<QueryOptions, THREAD_AGENT_FEATURES>> = {
    [QueryOptions.USE_STYLES]: THREAD_AGENT_FEATURES.USE_STYLES,
    [QueryOptions.WEB_SEARCH]: THREAD_AGENT_FEATURES.WEB_SEARCH,
    [QueryOptions.CANVAS]: THREAD_AGENT_FEATURES.CANVAS,
    [QueryOptions.CUSTOM_INSTRUCTIONS]: THREAD_AGENT_FEATURES.CUSTOM_INSTRUCTIONS,
    [QueryOptions.KNOWLEDGE_BASE]: THREAD_AGENT_FEATURES.KNOWLEDGE_BASE,
    [QueryOptions.IMAGE_ANALYSIS]: THREAD_AGENT_FEATURES.IMAGE_GENERATION,
    [QueryOptions.PROJECTS]: THREAD_AGENT_FEATURES.PROJECTS,
    [QueryOptions.LINKED_DOCS]: THREAD_AGENT_FEATURES.LINKED_DOCS,
    [QueryOptions.COMPARE_DOCS]: THREAD_AGENT_FEATURES.COMPARE_DOCS,
    [QueryOptions.KPIS]: THREAD_AGENT_FEATURES.KPIS,
    [QueryOptions.FORM_QUESTIONS]: THREAD_AGENT_FEATURES.FORM_QUESTIONS,
  };

  /** Tools menu items filtered by agent config: each tool is shown only if its feature is enabled (not excluded). */
  filteredToolsMenuItems = computed(() => {
    const items = this.toolsMenuItems();
    const isAskMode = this.selectedMode() === AGENT_MODE.ASK;

    return items.filter((item) => {
      // Hide Canvas, Knowledge Base Tool, and Project Tool in ASK mode
      if (isAskMode) {
        const hiddenToolsInAskMode = [
          QueryOptions.CANVAS,
          QueryOptions.KNOWLEDGE_BASE,
          QueryOptions.PROJECTS
        ];
        if (hiddenToolsInAskMode.includes(item.value as QueryOptions)) {
          return false;
        }
      }

      const featureKey = ThreadInputComponent.TOOL_FEATURE_KEYS[item.value as QueryOptions];
      if (featureKey == null) return true;
      return this.isFeatureEnabled(featureKey);
    });
  });

  // Map to store selected tools
  selectedToolsMap = new Map<any, CustomMenuItem>([
    [QueryOptions.USE_STYLES, {
      icon: this.SVG_ICONS.USE_STYLE,
      iconSource: IconSourceEnum.SVG,
      type: MenuItemType.SINGLE_SELECT,
      label: CHAT_STYLES.NORMAL,
      selectionTree: {
        [QueryOptions.USE_STYLES]: [CHAT_STYLES.NORMAL],
      },
      value: CHAT_STYLES.NORMAL
    }]
  ]);

  // Premium tools (added based on features)
  IMAGE_GENERATION_TOOL: CustomMenuItem = {
    label: 'Image Generation',
    value: QueryOptions.IMAGE_ANALYSIS,
    icon: 'image-plus',
    iconSource: IconSourceEnum.LUCIDE,
    subOptions: [],
  };

  PROJECTS_TOOL: CustomMenuItem = {
    label: 'Projects',
    value: QueryOptions.PROJECTS,
    icon: this.SVG_ICONS.PROJECT,
    iconSource: IconSourceEnum.SVG,
    subOptions: [],
    searchable: true,
  };

  KNOWLEDGE_BASE_TOOL: CustomMenuItem = {
    label: 'Knowledge Base',
    value: QueryOptions.KNOWLEDGE_BASE,
    icon: this.SVG_ICONS.KNOWLEDGE_BASE,
    iconSource: IconSourceEnum.SVG,
    subOptions: [],
    searchable: true,
  };

  LINKED_DOCS_TOOL: CustomMenuItem = {
    label: 'Linked Documents',
    value: QueryOptions.LINKED_DOCS,
    icon: this.SVG_ICONS.PROJECT,
    iconSource: IconSourceEnum.SVG,
    subOptions: [],
  };

  COMPARE_DOCS_TOOL: CustomMenuItem = {
    label: 'Compare Documents',
    value: QueryOptions.COMPARE_DOCS,
    icon: this.SVG_ICONS.PROJECT,
    iconSource: IconSourceEnum.SVG,
    subOptions: [],
  };

  KPIS_TOOL: CustomMenuItem = {
    label: 'KPIs',
    value: QueryOptions.KPIS,
    icon: 'gauge',
    iconSource: IconSourceEnum.LUCIDE,
    subOptions: [],
  };

  FORM_QUESTIONS_TOOL: CustomMenuItem = {
    label: 'Form Questions',
    value: QueryOptions.FORM_QUESTIONS,
    icon: 'circle-help',
    iconSource: IconSourceEnum.LUCIDE,
    type: MenuItemType.SINGLE_SELECT,
    subOptions: [],
  };

  // Connectors Menu
  connectorsMenuItems = signal<CustomMenuItem[]>([]);
  selectedConnectors = signal<string[]>([]);
  /** Tracks the browser connector's persisted toggle state from the API (true = enabled, false = disabled, null = no setting saved yet) */
  private browserConnectorSavedState = signal<boolean | null>(null);

  // ============================================
  // State - File Selection Dialog
  // ============================================
  openFileSelectionDialog = signal<boolean>(false);
  fileSelectionConfig = signal<FileSelectionConfig>({
    maxTotalUploadedAttachments: 50,
    maxTotalUploadedFileSize: 500 * 1000 * 1000,
    localUploadMaxFileSize: 100 * 1000 * 1000,
    title: 'Upload files',
    supportSubFolders: true,
    allowedFileTypes: [
      FileDropTypes.PDF,
      FileDropTypes.DOCX, FileDropTypes.DOC,
      FileDropTypes.XLSX,
      FileDropTypes.JPG, FileDropTypes.PNG, FileDropTypes.IMAGE, FileDropTypes.JPEG,
      FileDropTypes.MD, FileDropTypes.TEXT
    ],
    tabs: [
      {
        id: QueryOptions.WORKSPACE_KNOWLEDGE,
        label: 'Workspace knowledge',
        tooltip: 'Select from workspace knowledge base files',
        type: TabType.SELECT
      },
      {
        id: QueryOptions.PROJECT_KNOWLEDGE,
        label: 'Project knowledge',
        tooltip: 'Select from project knowledge base files',
        type: TabType.SELECT
      },
      {
        id: QueryOptions.PROJECT_FILES,
        label: 'Documents',
        tooltip: 'Select from project documents',
        type: TabType.SELECT
      },
      {
        id: QueryOptions.ADD_PHOTOS_AND_FILES,
        label: 'Files from local',
        tooltip: 'Select from local files',
        type: TabType.UPLOAD
      }
    ],
  });

  // Store KB and projects data
  knowledgeBases: any[] = [];
  projectKnowledgeBases: any[] = [];
  projects: any[] = [];

  // Dropdown options for file selection
  fileSelectionDropdownOptions = signal<DropdownOption[]>([]);

  // Subscriptions
  private embeddingsStatusSubscription?: Subscription;
  private linkedDocsReloadSubscription?: Subscription;

  // ============================================
  // File Selection Fetch Function
  // ============================================
  /**
   * Fetch function for file selection dialog
   * Handles loading files from KB or projects based on selected tab/dropdown
   */
  fileSelectionFetch: FetchFunction = (event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> => {
    if (event.tabId === QueryOptions.WORKSPACE_KNOWLEDGE) {
      // Workspace knowledge tab - load workspace-scoped knowledge base files
      if (event.dropdownId === 'all') {
        return this.fetchAllKnowledgeBaseFiles(event);
      } else if (event.dropdownId) {
        return this.fetchKnowledgeBaseFiles(event.dropdownId, event);
      } else {
        this.fileSelectionDropdownOptions.set([]);
        this.prepareFilesDropdownOptions();
        return of({ items: [], totalRecords: 0 });
      }
    } else if (event.tabId === QueryOptions.PROJECT_KNOWLEDGE) {
      // Project knowledge tab - load project-scoped knowledge base files
      if (event.dropdownId === 'all') {
        return this.fetchAllProjectKnowledgeFiles(event);
      } else if (event.dropdownId) {
        return this.fetchProjectKnowledgeFiles(event.dropdownId, event);
      } else {
        this.fileSelectionDropdownOptions.set([]);
        this.prepareProjectKnowledgeDropdownOptions();
        return of({ items: [], totalRecords: 0 });
      }
    } else if (event.tabId === QueryOptions.PROJECT_FILES) {
      // Documents tab - load project documents
      if (event.dropdownId === 'all') {
        // Load all documents from all projects
        return this.fetchAllProjectDocuments(event);
      } else if (event.dropdownId) {
        // Load documents from specific project
        return this.fetchProjectDocuments(event.dropdownId, event);
      } else {
        // No dropdown selected, clear old options and prepare new dropdown options
        this.fileSelectionDropdownOptions.set([]);
        this.prepareDocumentsDropdownOptions();
        return of({ items: [], totalRecords: 0 });
      }
    }
    return of({ items: [], totalRecords: 0 });
  }

  prepareFilesDropdownOptions() {
    const options: DropdownOption[] = [
      {
        id: 'all', label: 'All files',
        groupKey: 'metadata.kbId',
        groupLabel: 'metadata.kbName'
      }
    ];

    this.knowledgeBases.forEach(kb => {
      options.push({
        id: kb._id,
        label: kb.name,
        value: kb,
        groupKey: 'metadata.kbId',
        groupLabel: 'metadata.kbName'
      });
    });

    this.fileSelectionDropdownOptions.set(options);
  }

  prepareDocumentsDropdownOptions() {
    const options: DropdownOption[] = [
      {
        id: 'all', label: 'All documents',
        groupKey: 'metadata.projectId',
        groupLabel: 'metadata.projectName'
      }
    ];

    this.projects.forEach(project => {
      options.push({
        id: project._id,
        label: project.name,
        value: project,
        groupKey: 'metadata.projectId',
        groupLabel: 'metadata.projectName'
      });
    });

    this.fileSelectionDropdownOptions.set(options);
  }

  prepareProjectKnowledgeDropdownOptions() {
    const options: DropdownOption[] = [
      {
        id: 'all', label: 'All projects',
        groupKey: 'metadata.projectId',
        groupLabel: 'metadata.projectName'
      }
    ];

    this.projects.forEach(project => {
      options.push({
        id: project._id,
        label: project.name,
        value: project,
        groupKey: 'metadata.projectId',
        groupLabel: 'metadata.projectName'
      });
    });

    this.fileSelectionDropdownOptions.set(options);
  }

  fetchAllKnowledgeBaseFiles(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    this.prepareFilesDropdownOptions();

    return this.repositoryService.getFiles().pipe(
      map((res) => {
        const allFiles: FileItem[] = [];
        const files = res.data;
        const filteredFiles = files.filter(file => {
          if (file.type !== 'FILE') return false;
          if (file.documentKnowledge?.embeddingsStatus !== 'COMPLETED') return false;
          if (event.searchQuery && !file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())) {
            return false;
          }
          return true;
        });

        filteredFiles.forEach((file: any) => {
          const firstKb = file.knowledgeBases?.[0];
          const kbDetails = firstKb ? this.knowledgeBases.find(k => k._id === firstKb._id) : undefined;

          allFiles.push({
            id: file._id,
            name: file.name || 'Unknown',
            size: file.size,
            metadata: {
              kbId: kbDetails?._id ?? "file-repository",
              kbName: kbDetails?.name ?? "Files",
              originalFile: file
            }
          });
        });

        const startIndex = event.page * event.pageSize;
        const endIndex = startIndex + event.pageSize;
        const paginatedFiles = allFiles.slice(startIndex, endIndex);
        console.log('Paginated files:', paginatedFiles);
        return { items: paginatedFiles, totalRecords: allFiles.length };
      })
    );
  }

  fetchKnowledgeBaseFiles(kbId: string, event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    // Prepare dropdown options first
    this.prepareFilesDropdownOptions();

    const kb = this.knowledgeBases.find(k => k._id === kbId);
    if (!kb) {
      return of({ items: [], totalRecords: 0 });
    }

    let files: FileItem[] = [];
    if (kb.files && Array.isArray(kb.files)) {
      files = kb.files
        .filter((file: any) => {
          // Filter by search query if provided
          if (event.searchQuery && !file.file?.name?.toLowerCase().includes(event.searchQuery.toLowerCase())) {
            return false;
          }
          return true;
        })
        .map((file: any) => ({
          id: file.file?._id,
          name: file.file?.name || 'Unknown',
          size: file.file?.size,
          metadata: {
            kbId: kb._id,
            kbName: kb.name,
            originalFile: file.file
          }
        }));
    }

    // Handle pagination
    const startIndex = event.page * event.pageSize;
    const endIndex = startIndex + event.pageSize;
    const paginatedFiles = files.slice(startIndex, endIndex);

    return of({ items: paginatedFiles, totalRecords: files.length });
  }

  fetchAllProjectDocuments(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    this.prepareDocumentsDropdownOptions();

    const skip = event.page * event.pageSize;
    const limit = event.pageSize;

    const filters: any = {
      type: CONTRACT_TYPE.ALL,
      skip,
      limit,
      name: event.searchQuery || ''
    };

    return this.contractService.getContracts(filters).pipe(
      map((res) => {
        const documents: FileItem[] = [];
        let totalRecords = 0;

        if (res && res.success && res.data?.contracts) {
          res.data.contracts.forEach((doc: any) => {
            const project = this.projects.find(p => p._id === doc.projectId);
            const projectId = doc.projectId || doc.project?._id;
            const projectName = project?.name || doc.project?.name;

            documents.push({
              id: doc._id,
              name: doc.name || 'Unknown',
              size: doc?.fileSize || 0,
              metadata: {
                // Only include projectId and projectName if they exist
                ...(projectId && { projectId }),
                ...(projectName && { projectName }),
                originalDoc: doc
              }
            });
          });
          totalRecords = res.data?.total || 0;
        }

        const paginatedDocs = documents;

        return { items: paginatedDocs, totalRecords };
      })
    );
  }

  fetchProjectDocuments(projectId: string, event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    // Prepare dropdown options first
    this.prepareDocumentsDropdownOptions();

    const project = this.projects.find(p => p._id === projectId);
    if (!project) {
      return of({ items: [], totalRecords: 0 });
    }

    return this.contractService.getContracts({
      type: CONTRACT_TYPE.ALL,
      skip: event.page * event.pageSize,
      limit: event.pageSize,
      name: event.searchQuery || '',
      projectId: projectId
    }).pipe(
      map((res) => {
        if (res.success && res.data.contracts) {
          const documents: FileItem[] = res.data.contracts.map((doc: any) => ({
            id: doc._id,
            name: doc.name || 'Unknown',
            metadata: {
              projectId: project._id || 'all-docs',
              projectName: project.name,
              originalDoc: doc
            }
          }));

          return { items: documents, totalRecords: res.data.total || 0 };
        } else {
          return { items: [], totalRecords: 0 };
        }
      })
    );
  }

  fetchAllProjectKnowledgeFiles(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    this.prepareProjectKnowledgeDropdownOptions();

    if (event.parentFolderId) {
      return this.repositoryService.getFolderDetails(event.parentFolderId).pipe(
        map((res: any) => {
          const childs = res?.data?.childs ?? res?.childs ?? [];
          const filtered = childs.filter((f: any) => !event.searchQuery || f.name?.toLowerCase().includes(event.searchQuery!.toLowerCase()));
          const allItems: FileItem[] = filtered.map((file: any) => {
            if (file.type === 'FOLDER') return { id: file._id, name: file.name || 'Unknown', isFolder: true, metadata: {} };
            const project = this.projects.find(p => p._id === (file.project?._id ?? file.project));
            return { id: file._id, name: file.name || 'Unknown', size: file.size, metadata: { projectId: project?._id ?? 'no-project', projectName: project?.name ?? 'No project', originalFile: file, isProjectKnowledge: true } };
          });
          const start = event.page * event.pageSize;
          return { items: allItems.slice(start, start + event.pageSize), totalRecords: allItems.length };
        }),
        catchError(() => of({ items: [], totalRecords: 0 }))
      );
    }

    return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT }).pipe(
      map((res) => {
        const raw = (res as any)?.data ?? [];
        const filtered = raw.filter((f: any) => !event.searchQuery || f.name?.toLowerCase().includes(event.searchQuery!.toLowerCase()));
        const allFiles: FileItem[] = filtered.map((file: any) => {
          if (file.type === 'FOLDER') return { id: file._id, name: file.name || 'Unknown', isFolder: true, metadata: {} };
          const project = this.projects.find(p => p._id === (file.project?._id ?? file.project));
          return { id: file._id, name: file.name || 'Unknown', size: file.size, metadata: { projectId: project?._id ?? 'no-project', projectName: project?.name ?? 'No project', originalFile: file, isProjectKnowledge: true } };
        });
        const start = event.page * event.pageSize;
        return { items: allFiles.slice(start, start + event.pageSize), totalRecords: allFiles.length };
      })
    );
  }

  fetchProjectKnowledgeFiles(projectId: string, event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    this.prepareProjectKnowledgeDropdownOptions();

    const project = this.projects.find(p => p._id === projectId);
    if (!project) return of({ items: [], totalRecords: 0 });

    const allKnowledgeFolderId = ALL_KNOWLEDGE_FOLDER_PREFIX + projectId;

    // Navigated into "All Knowledge" virtual folder — show all project files flat
    if (event.parentFolderId === allKnowledgeFolderId) {
      return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT, project: projectId }).pipe(
        map((res: any) => {
          const raw = res?.data ?? [];
          const items: FileItem[] = raw
            .filter((f: any) => f.type === 'FILE' && (!event.searchQuery || f.name?.toLowerCase().includes(event.searchQuery!.toLowerCase())))
            .map((file: any) => ({ id: file._id, name: file.name || 'Unknown', size: file.size, metadata: { projectId: project._id, projectName: project.name, originalFile: file, isProjectKnowledge: true } }));
          const start = event.page * event.pageSize;
          return { items: items.slice(start, start + event.pageSize), totalRecords: items.length };
        }),
        catchError(() => of({ items: [], totalRecords: 0 }))
      );
    }

    // Navigated into a specific KB store folder — show its files
    if (event.parentFolderId) {
      return this.knowledgeBaseService.getKnowledgeBaseStoreById(event.parentFolderId).pipe(
        map((res: any) => {
          const store = res?.data ?? res;
          if (!store?.files) return { items: [], totalRecords: 0 };
          const breadcrumbLabel = `${project.name} > ${store.name || 'Unknown'}`;
          let list: FileItem[] = store.files.map((item: any) => ({ id: item.file?._id, name: item.file?.name || 'Unknown', size: item.file?.size, metadata: { projectId: project._id, projectName: project.name, kbId: store._id, kbName: store.name, breadcrumbLabel, originalFile: item.file, isProjectKnowledge: true } }));
          if (event.searchQuery) list = list.filter(f => f.name?.toLowerCase().includes(event.searchQuery!.toLowerCase()));
          const start = event.page * event.pageSize;
          return { items: list.slice(start, start + event.pageSize), totalRecords: list.length };
        }),
        catchError(() => of({ items: [], totalRecords: 0 }))
      );
    }

    // Root level — show KB stores as folders + "All Knowledge" virtual folder
    return this.knowledgeBaseService.getKnowledgeBaseStore({ scope: KNOWLEDGE_SCOPE.PROJECT, project: projectId, skip: event.page === 0 ? 0 : event.page * event.pageSize - 1, limit: event.page === 0 ? event.pageSize - 1 : event.pageSize, search: event.searchQuery || '' }).pipe(
      map((res) => {
        const stores = res?.data?.stores ?? [];
        const storeItems: FileItem[] = stores.map((s: any) => ({ id: s._id, name: s.name || 'Unknown', isFolder: true, metadata: { kbId: s._id, kbName: s.name } }));
        const allKnowledgeFolder: FileItem = { id: allKnowledgeFolderId, name: 'All Knowledge', isFolder: true, metadata: {} };
        const items: FileItem[] = event.page === 0 ? [allKnowledgeFolder, ...storeItems] : storeItems;
        return { items, totalRecords: (res?.data?.total ?? 0) + 1 };
      }),
      catchError(() => of({ items: [], totalRecords: 0 }))
    );
  }

  // ============================================
  // Constants
  // ============================================
  readonly THREAD_METADATA_STATUS = THREAD_METADATA_STATUS;
  readonly IconSourceEnum = IconSourceEnum;

  // ============================================
  // Private Properties
  // ============================================
  private enterShortcutId?: string;
  private restoredAttachmentsReceived = signal<boolean>(false);

  private restoredAttachmentsEnriched = false;
  private restoredQueryApplied = false;
  private restoredModeApplied = false;

  // ============================================
  // Constructor & Dependency Injection
  // ============================================
  constructor(
    private keyboardShortcutService: KeyboardShortcutService,
    public threadMessagesService: ThreadMessagesService,
    public voiceService: VoiceRecognitionService,
    private authService: AuthenticationService,
    private features: FeatureFlagService,
    private knowledgeBaseService: KnowledgeBaseService,
    private projectService: ProjectService,
    private repositoryService: RepositoryService,
    private contractService: ContractService,
    private linkDocumentService: LinkDocumentService,
    private generalAgentService: GeneralAgentService,
    private socketService: SocketService,
    private router: Router,
    private toastService: ToastService,
    private browserExtensionService: BrowserExtensionService,
    private knowledgeMappingService: KnowledgeMappingService,
    private integrationsService: IntegrationsService,
    private subscriptionState: SubscriptionStateService,

  ) {
    // Add premium tools based on feature flags
    this.addPremiumTools();
    this.buildOptionsMenuItems();

    inject(DestroyRef).onDestroy(() => {
      // Cleanup happens automatically for effects typically, but ensuring scope
    });

    effect(() => {
      const providerValue = this.provider();
      if (providerValue) {
        this.selectedProviderSignal.set(providerValue);
        const model = AVAILABLE_MODELS.find((m: AIModelProviderOption) => m.value === providerValue);
        if (model) {
          this.selectedModel.set(model);
        }
      }
    });

    // Keep modelOptions in sync with the subscription's allowed models.
    // When allowed models change (e.g. after a billing cycle refresh), refilter
    // the list and fall back the selected provider if it is no longer available.
    effect(() => {
      const allowed = this.subscriptionState.allowedModels();
      const filtered = AVAILABLE_MODELS.filter(m => allowed.includes(m.value));
      this.modelOptions.set(filtered);

      // If the current selection is no longer in the allowed list, switch to the first available.
      if (filtered.length > 0 && !filtered.some(m => m.value === this.selectedProviderSignal())) {
        this.selectedProviderSignal.set(filtered[0].value);
        this.selectedModel.set(filtered[0]);
      }
    });

    effect(() => {
      const docId = this.documentId();
      if (docId) {
        this.loadLinkedDocs();
      }
    });

    effect(() => {
      if (this.compareDocuments()) {
        this.loadCompareDocsTool();
      }
    });

    effect(() => {
      if (this.kpiDocuments()) {
        this.loadKpisTool();
      }
    });

    effect(() => {
      if (this.formQuestions()) {
        this.loadFormQuestionsTool();
      }
    });

    effect(() => {
      const questionId = this.selectedFormQuestionId();
      if (!questionId) return;
      const questions = this.formQuestions();
      const match = questions.find(q => q._id === questionId);
      if (match) {
        const selectionTree = { [QueryOptions.FORM_QUESTIONS]: [questionId] };
        this.selectedToolsMap.set(QueryOptions.FORM_QUESTIONS, {
          label: match.name,
          value: questionId,
          icon: 'circle-help',
          iconSource: IconSourceEnum.LUCIDE,
          type: MenuItemType.SINGLE_SELECT,
          selectionTree,
        });
      }
    });

    // Effect A: When navigating between existing threads, clear stale instructions immediately.
    // When transitioning from new chat (previousThreadId='') keep the current value so the
    // chip doesn't flash empty while loadThread is in-flight.
    effect(() => {
      const id = this.threadId();
      if (id && id !== this.previousThreadId) {
        const previousId = this.previousThreadId;
        const wasNewChat = previousId === '';
        this.previousThreadId = id;
        if (!wasNewChat) {
          // Switching threads reuses this component, so ngOnDestroy (which also
          // persists the draft) never fires. Save the outgoing thread's composer
          // text here so it is restored when the user returns to it.
          const outgoingDraft = untracked(() => this.messageText());
          this.generalAgentService.saveDraftPrompt(previousId, outgoingDraft).subscribe();
          this.customInstructions.set('');
          // Clear so the incoming thread shows its own draft (restored by Effect C
          // below) instead of the previous thread's text bleeding across.
          this.messageText.set('');
        }
      }
    });

    // Effect: Forward composer text to parent so it can debounce for soft-nudge.
    effect(() => {
      this.queryChange.emit(this.messageText());
    });

    // Effect: Restore pending attachments, query, and mode from restoredInputState.
    // attachments: null → reset (thread change); non-null array → apply once.
    // query/mode: apply once when non-null, reset flag when null so re-navigation works.
    effect(() => {
      const state = this.restoredInputState();

      const attachments = state?.attachments ?? null;
      if (attachments === null) {
        if (this.restoredAttachmentsReceived()) {
          this.restoredAttachmentsReceived.set(false);
          this.restoredAttachmentsEnriched = false;
          this.attachments.set([]);
        }
      } else if (!this.restoredAttachmentsReceived()) {
        this.restoredAttachmentsReceived.set(true);
        this.attachments.set(attachments);
        if (state?.largeFileAttached) {
          this.largeFileAttached.set(true);
        }
      } else if (!this.restoredAttachmentsEnriched) {
        const enrichedById = new Map(attachments.map(a => [a._id, a]));
        const current = untracked(() => this.attachments());
        let changed = false;
        const merged = current.map(a => {
          const e = enrichedById.get(a._id);
          if (e?.refDocModel && e.refDocModel !== a.refDocModel) {
            changed = true;
            return { ...a, ...e };
          }
          return a;
        });
        if (changed) {
          this.restoredAttachmentsEnriched = true;
          this.attachments.set(merged);
        }
      }

      const query = state?.query;
      if (query && !this.restoredQueryApplied) {
        this.restoredQueryApplied = true;
        this.messageText.set(query);
      } else if (!query) {
        if (this.restoredQueryApplied) {
          // Parent cleared the query (e.g. messages already exist on this thread).
          // Clear the pre-populated text so the input does not show stale content.
          this.messageText.set('');
        }
        this.restoredQueryApplied = false;
      }

      const mode = state?.mode;
      if (mode && !this.restoredModeApplied) {
        this.restoredModeApplied = true;
        this.selectedMode.set(mode);
      } else if (!mode) {
        this.restoredModeApplied = false;
      }

      // Files queued on the new-chat page while the thread was being created
      // arrive here once the thread is active — upload them to this thread.
      const stagedFiles = state?.stagedFiles;
      if (stagedFiles?.length && this.threadMetadataId() && !this.restoredStagedFilesUploaded) {
        this.restoredStagedFilesUploaded = true;
        this.uploadLocalFiles(stagedFiles);
      } else if (!stagedFiles?.length) {
        this.restoredStagedFilesUploaded = false;
      }
    });

    // Effect B: Apply restored instructions once they are actually loaded (non-null).
    // This covers: refresh, new-chat→active-thread, and thread-to-thread navigation.
    effect(() => {
      const restored = this.restoredCustomInstructions();
      const id = this.threadId();
      if (id && restored !== null) {
        this.customInstructions.set(restored);
      }
    });

    // Effect C: Restore unsent draft prompt from thread metadata.
    // Only sets messageText when a non-empty draft is loaded (null = still loading).
    effect(() => {
      const draft = this.restoredInputState()?.draftPrompt;
      const id = this.threadId();
      if (id && draft) {
        this.messageText.set(draft);
        if (this.messageTextarea?.nativeElement) {
          this.messageTextarea.nativeElement.value = draft;
        }
      }
    });

    /**
     * Watches extension install status and the persisted saved state to determine
     * the My Browser connector menu item type and toggle state.
     * - Extension NOT installed → show "Connect" button (links to Chrome Web Store)
     * - Extension installed + saved state null (no doc) → toggle ON (default)
     * - Extension installed + saved state true → toggle ON
     * - Extension installed + saved state false → toggle OFF (user explicitly disabled)
     */
    effect(() => {
      const isExtensionInstalled = this.browserExtensionService.isExtensionInstalled();
      const savedState = this.browserConnectorSavedState();
      this.connectorsMenuItems.update(menuItems => menuItems.map(menuItem => {
        if (menuItem.value === MISC_CONNECTORS.MY_BROWSER) {
          if (!isExtensionInstalled) {
            menuItem = {
              ...menuItem,
              type: MenuItemType.BUTTON,
              buttonLabel: 'Connect',
              buttonClass: 'theme-text border-none'
            }
          }
          else {
            menuItem = {
              ...menuItem,
              type: MenuItemType.TOGGLE,
              toggleState: savedState !== false,
            }
          }
        }
        return menuItem;
      }));
    });

    // Debounce nudge visibility: show after 1500ms to avoid flicker while typing,
    // hide immediately when trigger clears (dismiss, mode switch, send).
    effect(() => {
      const shouldShow = this.agentNudgeTrigger();
      if (!shouldShow) {
        if (this.nudgeDebounceTimer) {
          clearTimeout(this.nudgeDebounceTimer);
          this.nudgeDebounceTimer = null;
        }
        this.agentNudgeVisible.set(false);
      } else if (!this.nudgeDebounceTimer) {
        this.nudgeDebounceTimer = setTimeout(() => {
          this.nudgeDebounceTimer = null;
          this.creditNudgeMode.set(null);
          this.agentNudgeVisible.set(true);
        }, 1500);
      }
    });

    // Clear credit nudge whenever the user manually switches mode.
    effect(() => {
      const mode = this.selectedMode();
      const nudge = this.creditNudgeMode();
      if ((nudge === CREDIT_NUDGE_MODE.TO_AGENT && mode === AGENT_MODE.AGENTIC) ||
        (nudge === CREDIT_NUDGE_MODE.TO_ASK && mode === AGENT_MODE.ASK)) {
        this.creditNudgeMode.set(null);
      }
    });

    // Emit settled (non-temp) attachments so the parent can preserve them across
    // component recreation (e.g. browser resize crossing MOBILE_BREAKPOINT).
    // Gated on restoredAttachmentsReceived so the initial [] on component creation
    // does not wipe the parent's stored session files before they can be passed back.
    effect(() => {
      if (!this.restoredAttachmentsReceived()) return;
      const settled = this.attachments().filter(a => !a._id.startsWith('temp-'));
      this.attachmentsChange.emit(settled);
    });
  }

  // ============================================
  // Lifecycle Hooks
  // ============================================
  ngOnInit(): void {
    // Get current user (for empty state greeting)
    this.authService.currentUser.subscribe(user => {
      this.currentUser.set(user ?? null);
    });

    // Subscribe to voice transcripts
    this.voiceService.transcript$.subscribe(text => {
      this.tempTranscript.update(current =>
        current ? current + ' ' + text : text
      );
    });

    // Sync provider input with internal state
    const providerValue = this.provider();
    this.selectedProviderSignal.set(providerValue);
    const model = AVAILABLE_MODELS.find((m: AIModelProviderOption) => m.value === providerValue);
    if (model) {
      this.selectedModel.set(model);
    }

    // Setup tools and options
    this.setupToolAndOptions();

    // reload linked-doc menu immediately when link/unlink happens elsewhere.
    this.linkedDocsReloadSubscription = this.linkDocumentService.reloadLinkedFiles.subscribe((sourceDocumentId: any) => {
      const currentDocId = this.documentId();
      if (!currentDocId) return;
      if (sourceDocumentId && sourceDocumentId !== currentDocId) return;
      this.loadLinkedDocs();
    });

  }

  /**
   * Setup tools and options on initialization
   */
  private setupToolAndOptions(): void {
    this.getModels();
    if (this.isFeatureEnabled(THREAD_AGENT_FEATURES.KNOWLEDGE_BASE)) {
      this.loadKnowledgeBasesForDropdown();
    }
    if (this.isFeatureEnabled(THREAD_AGENT_FEATURES.PROJECTS)) {
      this.loadProjectsForDropdown();
    }
    if (this.isFeatureEnabled(THREAD_AGENT_FEATURES.LINKED_DOCS)) {
      this.loadLinkedDocs();
    }
    if (this.isFeatureEnabled(THREAD_AGENT_FEATURES.CONNECTORS)) {
      this.getConnectors();
    }
    this.setupSocketListeners();
  }

  /**
   * Get available models from API
   */
  private getModels(): void {
    this.generalAgentService.getModels().subscribe((res: any) => {
      if (res.success) {
        this.modelOptions.update(modelOptions => modelOptions.map(model => {
          if (res.data[model.value]) {
            model.model = res.data[model.value].model;
          }
          return model;
        }));
      }
    });
  }

  ngAfterViewInit(): void {
    this.registerEnterShortcut();
  }

  ngOnDestroy(): void {
    const threadId = this.threadId();
    if (threadId) {
      this.generalAgentService.saveDraftPrompt(threadId, this.messageText()).subscribe();
    }

    this.persistStagedFilesOnDestroy();

    if (this.enterShortcutId) {
      this.keyboardShortcutService.unregister(this.enterShortcutId);
    }

    if (this.nudgeDebounceTimer) {
      clearTimeout(this.nudgeDebounceTimer);
    }

    // Unsubscribe from socket listeners
    if (this.embeddingsStatusSubscription) {
      this.embeddingsStatusSubscription.unsubscribe();
    }
    if (this.linkedDocsReloadSubscription) {
      this.linkedDocsReloadSubscription.unsubscribe();
    }

    // Stop voice recording if active
    if (this.voiceService.isRecording()) {
      this.voiceService.stop();
    }

    // Clean up microphone resources
    this.voiceService.cleanupAudioResources();
  }

  // ============================================
  // Keyboard Shortcuts
  // ============================================
  /**
   * Register Enter key shortcut to send message
   * Shift+Enter creates new line
   */
  private registerEnterShortcut(): void {

    if (!this.messageTextarea?.nativeElement) {
      return;
    }
    const shortcutConfig: KeyboardShortcutConfig = {
      id: 'send-message-enter',
      keys: 'Enter',
      handler: (event: KeyboardEvent) => {
        const isTextareaFocused = document.activeElement === this.messageTextarea?.nativeElement;

        if (isTextareaFocused && !event.shiftKey) {
          event.preventDefault();
          this.sendMessage();
          return true;
        }
        return false;
      },
      description: 'Send message',
      context: 'message-input',
      preventDefault: false
    };

    this.enterShortcutId = this.keyboardShortcutService.register(shortcutConfig);
  }

  // ============================================
  // Message Actions
  // ============================================
  /**
   * Send message to chat
   */
  sendMessage(): void {
    const text = this.messageText().trim();
    if (this.deferredSendPayload) {
      return;
    }
    if (!text || this.disableSend() || this.isLoadingOrSending()) {
      return;
    }

    const mode = this.selectedMode();
    const remaining = mode === AGENT_MODE.AGENTIC
      ? this.userCreditsState.agentRemaining()
      : this.userCreditsState.askRemaining();

    if (mode === AGENT_MODE.AGENTIC) {
      if (remaining <= 0) {
        const askRemaining = this.userCreditsState.askRemaining();
        if (askRemaining >= 0.01) {
          this.agentNudgeVisible.set(false);
          this.creditNudgeMode.set(CREDIT_NUDGE_MODE.TO_ASK);
        } else {
          this.toastService.error(`You've run out of credits. Request more to continue.`);
        }
        return;
      }
      if (remaining < 3) {
        this.toastService.warn(`Low credits (${remaining} remaining). This task may not complete.`);
      }
    } else {
      if (remaining < 0.01) {
        const agentRemaining = this.userCreditsState.agentRemaining();
        if (agentRemaining > 0) {
          this.agentNudgeVisible.set(false);
          this.creditNudgeMode.set(CREDIT_NUDGE_MODE.TO_AGENT);
        } else {
          this.toastService.error(`You've run out of credits. Request more to continue.`);
        }
        return;
      }
      if (remaining < 2) {
        this.toastService.warn(`Low credits (${remaining} remaining). Request more soon.`);
      }
    }

    // Stop voice recording if active
    if (this.voiceService.isRecording()) {
      this.voiceService.stop();
      this.voiceService.cleanupAudioResources();
    }

    const tools = this.mergeToolSelectionTrees(this.getSelectedToolsOptions())
    if (this.customInstructions()) {
      tools.push({
        [QueryOptions.CUSTOM_INSTRUCTIONS]: this.customInstructions()
      })
    }

    if (this.stagedFilesMap.size > 0) {
      const stagedIds = Array.from(this.stagedFilesMap.keys());
      const filesToUpload = Array.from(this.stagedFilesMap.values());

      this.pendingSendPayload = {
        query: text,
        mode: this.selectedMode(),
        provider: this.selectedModel()?.value || LLM_PROVIDERS.ANTHROPIC,
        tools,
        connectors: this.selectedMode() === AGENT_MODE.AGENTIC ? this.getSelectedConnectors() : [],
      };

      this.stagedFilesMap.clear();
      this.attachments.update(current => current.filter(a => !stagedIds.includes(a._id)));
      this.uploadLocalFiles(filesToUpload);
      return;
    }

    const payload: ChatMessagePayload = {
      query: text,
      provider: this.selectedModel()?.value || LLM_PROVIDERS.ANTHROPIC,
      mode: this.selectedMode(),
      attachments: this.attachments().map(attachment => ({
        name: attachment.name || '',
        type: attachment.type || '',
        _id: attachment._id || '',
        docModel: attachment.docModel,
        attachmentData: attachment.attachmentData
      })),
      tools,
      connectors: this.selectedMode() == AGENT_MODE.AGENTIC ? this.getSelectedConnectors() : [],
      ...((this.uploadedThreadId() || this.threadId()) && {
        threadId: this.uploadedThreadId() || this.threadId()
      }),
      ...(this.threadMetadata().length > 0 && { threadMetadataId: this.threadMetadata()[0]._id })
    };

    this.sendMessageEvent.emit(payload);
    this.clearInput();
  }

  /**
   * Pause/stop the current agent execution
   */
  pauseThread(): void {
    this.pauseThreadEvent.emit();
  }

  dismissNudge(): void {
    this.nudgeDismissed.set(true);
  }

  switchToAgentMode(): void {
    this.selectedMode.set(AGENT_MODE.AGENTIC);
    this.nudgeDismissed.set(true);
  }

  switchToAskMode(): void {
    this.selectedMode.set(AGENT_MODE.ASK);
    this.creditNudgeMode.set(null);
  }

  switchToAgentModeFromCredit(): void {
    this.selectedMode.set(AGENT_MODE.AGENTIC);
    this.creditNudgeMode.set(null);
  }

  dismissCreditNudge(): void {
    this.creditNudgeMode.set(null);
  }

  /**
   * Clear the message input
   */
  clearInput(): void {

    this.messageText.set('');
    this.attachments.set([]);
    this.nudgeDismissed.set(false);
    this.creditNudgeMode.set(null);
    this.largeFileAttached.set(false);
    this.uploadedThreadId.set('');
    this.threadMetadata.set([]);
    this.stagedFilesMap.clear();
    this.creatingThreadFromUpload = false;
    this.pendingSendPayload = null;
    this.deferredSendPayload = null;
    this.uploadedAttachments.set([]);
    this.resetUserSelectedOptions();
    if (this.messageTextarea?.nativeElement) {
      this.messageTextarea.nativeElement.value = '';
    }
  }

  openEditCustomInstructions(): void {
    this._instructionsBeforeEdit = this.customInstructions();
    this.openCustomInstructionsDialog.set(true);
  }

  /**
   * Set message text programmatically
   */
  setMessageText(text: string): void {
    this.messageText.set(text ?? '');
    if (this.messageTextarea?.nativeElement) {
      this.messageTextarea.nativeElement.value = text ?? '';
    }
  }

  // ============================================
  // Model Selection
  // ============================================
  onModelChange(model: AIModelProviderOption): void {
    this.selectedModel.set(model);
    if (model.value !== this.selectedProviderSignal()) {
      this.selectedProviderSignal.set(model.value);
    }
  }

  // ============================================
  // Token Usage Display Methods
  // ============================================
  /**
   * Generate SVG path for pie chart displaying token usage
   */
  getPieChartPath(): string {
    const percentage = this.tokenUsagePercentage();
    const size = 16;
    const center = size / 2;
    const radius = size / 2 - 1;

    if (percentage >= 100) {
      return '';
    }

    if (percentage <= 0) {
      return '';
    }

    const angle = (percentage / 100) * 360;
    return this.createPiePath(center, center, radius, 0, angle);
  }

  /**
   * Create SVG path for pie chart segment
   */
  private createPiePath(cx: number, cy: number, radius: number, startAngle: number, endAngle: number): string {
    const startRad = (startAngle - 90) * (Math.PI / 180);
    const endRad = (endAngle - 90) * (Math.PI / 180);

    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);

    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  }

  /**
   * Get tooltip text showing token usage details
   */
  getTooltipText(): string {
    const percentage = this.tokenUsagePercentage();
    const total = this.totalTokens();
    const max = this.maxTokens();
    return `${percentage.toFixed(1)}% - ${new NumberAbbreviationPipe().transform(total)} / ${new NumberAbbreviationPipe().transform(max)} tokens`;
  }

  // ============================================
  // Voice Input
  // ============================================
  /**
   * Start voice recording
   */
  async startVoiceRecording(): Promise<void> {
    // Clear previous transcript
    this.tempTranscript.set('');

    // Show loading if microphone needs initialization
    if (this.voiceService.needsMicrophoneInit()) {
      this.isVoiceStarting.set(true);
    }

    try {
      await this.voiceService.start();
    } catch (error) {
      console.error('Failed to start voice recording:', error);
      this.isVoiceStarting.set(false);
    }
  }

  /**
   * Cancel voice recording without using transcript
   */
  cancelVoiceRecording(): void {
    this.voiceService.stop();
    this.tempTranscript.set('');
    this.isVoiceStarting.set(false);
    this.voiceService.cleanupAudioResources();
  }

  /**
   * Confirm voice recording and add transcript to message
   */
  confirmVoiceRecording(): void {
    this.isConfirmingVoice.set(true);
    this.voiceService.stop();

    // Wait for final transcript
    setTimeout(() => {
      this.isVoiceStarting.set(false);
      this.isConfirmingVoice.set(false);

      const transcript = this.tempTranscript();
      if (transcript?.trim()) {
        const currentText = this.messageText();
        this.messageText.set(
          currentText ? currentText + ' ' + transcript : transcript
        );
        this.tempTranscript.set('');
      }

      this.voiceService.cleanupAudioResources();

      setTimeout(() => this.messageTextarea?.nativeElement?.focus());
    }, 500);
  }

  // ============================================
  // Menu Handlers
  // ============================================
  /**
   * Check if user has integrations feature
   */
  hasIntegrationsFeature(): boolean {
    return this.features.checkFeature(UI_FEATURE.INTEGRATIONS_PREMIUM).allowed;
  }

  /**
   * Open options menu panel
   */
  openOptionsPanel(event: Event): void {
    event.stopPropagation();
    this.toolsMenu?.hide();
    this.connectorsMenu?.hide();
    this.optionsMenu?.toggle(event);
  }

  /**
   * Open tools menu panel
   */
  openToolsPanel(event: Event): void {
    event.stopPropagation();
    this.optionsMenu?.hide();
    this.connectorsMenu?.hide();
    this.toolsMenu?.toggle(event);
  }

  /**
   * Open connectors menu panel
   */
  openConnectorsPanel(event: Event): void {
    event.stopPropagation();
    this.optionsMenu?.hide();
    this.toolsMenu?.hide();
    this.connectorsMenu?.toggle(event);
  }

  /**
   * Handle option selection from options menu
   */
  onOptionSelect(option: CustomMenuItem): void {


    // Prevent file upload when disabled
    if (this.disabled() && (
      option.value === QueryOptions.KNOWLEDGE_BASE ||
      option.value === QueryOptions.PROJECT_FILES ||
      option.value === QueryOptions.ADD_PHOTOS_AND_FILES
    )) {
      return;
    }

    // Open file selection dialog for all file-related options
    switch (option.value) {
      case QueryOptions.KNOWLEDGE_BASE:
      case QueryOptions.PROJECT_FILES:
      case QueryOptions.ADD_PHOTOS_AND_FILES:
        this.openFileSelectionDialog.set(true);
        this.initializeFileSelection(option.value);
        break;
    }

    this.selectedOptionsChange.emit([...this.selectedOptions()]);
  }

  /**
   * Handle tool selection from tools menu
   */
  onToolsOptionSelect(option: CustomMenuItem): void {


    // Handle Custom Instructions dialog
    if (option.value === QueryOptions.CUSTOM_INSTRUCTIONS) {
      this.openEditCustomInstructions();
      return;
    }

    // Handle Image Analysis mutual exclusivity
    if (option.value === QueryOptions.IMAGE_ANALYSIS) {
      const imageAnalysisKey = QueryOptions.IMAGE_ANALYSIS;
      if (this.selectedToolsMap.has(imageAnalysisKey)) {
        // Toggle off: remove Image Analysis
        this.removeImageAnalysis();
      } else {
        // Clear all other tools and add Image Analysis
        this.selectedToolsMap.clear();
        // Reset all toggleStates to false
        this.toolsMenuItems.update(items =>
          items.map(item =>
            item.type === MenuItemType.TOGGLE ? { ...item, toggleState: false } : item
          )
        );
        this.selectedToolsMap.set(imageAnalysisKey, option);
        // Remove Anthropic from available models
        this.modelOptions.update(modelOptions =>
          modelOptions.filter(model => model.value !== LLM_PROVIDERS.ANTHROPIC)
        );
        // Switch to OpenAI if currently on Anthropic
        if (this.selectedProviderSignal() === LLM_PROVIDERS.ANTHROPIC) {
          this.selectedProviderSignal.set(LLM_PROVIDERS.OPENAI);
        }
      }
      this.selectedToolsOptionsChange.emit(this.getSelectedToolsOptions());
      return;
    }

    // If any other tool is being selected, check if Image Analysis is currently selected
    if (this.selectedToolsMap.has(QueryOptions.IMAGE_ANALYSIS)) {
      this.removeImageAnalysis();
    }

    // Determine the key for the map
    let mapKey: any;
    if (option.type === MenuItemType.SINGLE_SELECT && option.selectionTree) {
      mapKey = Object.keys(option.selectionTree)[0];
    } else {
      mapKey = option.value;
    }

    // Check if already selected
    if (this.selectedToolsMap.has(mapKey)) {
      // Remove if it's the same option, or update if it's a different sub option
      const existingOption = this.selectedToolsMap.get(mapKey);
      if (existingOption?.value === option.value) {
        // Same option - deselect it
        this.selectedToolsMap.delete(mapKey);

        // Update toggle state if applicable
        if (option.type === MenuItemType.TOGGLE) {
          this.toolsMenuItems.update(items =>
            items.map(item =>
              item.value === option.value
                ? { ...item, toggleState: false }
                : item
            )
          );
        }
      } else {
        // Different sub option - update selection
        this.selectedToolsMap.set(mapKey, option);
      }
    } else {
      // Add new selection
      this.selectedToolsMap.set(mapKey, option);

      // Update toggle state if applicable
      if (option.type === MenuItemType.TOGGLE) {
        this.toolsMenuItems.update(items =>
          items.map(item =>
            item.value === option.value
              ? { ...item, toggleState: true }
              : item
          )
        );
      }
    }

    this.selectedToolsOptionsChange.emit(this.getSelectedToolsOptions());
  }

  /**
   * Remove Image Analysis tool and restore Anthropic model
   */
  removeImageAnalysis(): void {
    this.selectedToolsMap.delete(QueryOptions.IMAGE_ANALYSIS);
    const allowed = this.subscriptionState.allowedModels();
    this.modelOptions.set(AVAILABLE_MODELS.filter(m => allowed.includes(m.value)));
  }

  /**
   * Reset all selected tools
   */
  resetTools(): void {
    this.selectedToolsMap.clear();
    this.selectedToolsOptionsChange.emit([...this.getSelectedToolsOptions()]);
  }

  private resetUserSelectedOptions(): void {
    const preservedFormQuestion = this.selectedToolsMap.get(QueryOptions.FORM_QUESTIONS);
    const hadImageAnalysis = this.selectedToolsMap.has(QueryOptions.IMAGE_ANALYSIS);

    this.selectedToolsMap.clear();
    if (preservedFormQuestion) {
      this.selectedToolsMap.set(QueryOptions.FORM_QUESTIONS, preservedFormQuestion);
    }

   
    this.toolsMenuItems.update(items =>
      items.map(item => item.type === MenuItemType.TOGGLE ? { ...item, toggleState: false } : item)
    );

  
    if (hadImageAnalysis) {
      this.removeImageAnalysis();
    }

    this.selectedConnectors.set([]);

    this.selectedToolsOptionsChange.emit(this.getSelectedToolsOptions());
    this.selectedConnectorsChange.emit([...this.getSelectedConnectorsOptions()]);
  }

  /**
   * Get selected tools as array
   */
  getSelectedToolsOptions(): CustomMenuItem[] {
    return Array.from(this.selectedToolsMap.values());
  }

  private mergeToolSelectionTrees(options: CustomMenuItem[]): Record<string, any>[] {
    const tools: Record<string, any>[] = [];
    const arrayEntryByKey = new Map<string, Record<string, any>>();
    for (const option of options) {
      const tree = option.selectionTree;
      if (!tree) continue;
      for (const [key, value] of Object.entries(tree)) {
        if (Array.isArray(value)) {
          let entry = arrayEntryByKey.get(key);
          if (!entry) {
            entry = { [key]: [] as any[] };
            arrayEntryByKey.set(key, entry);
            tools.push(entry);
          }
          const bucket = entry[key] as any[];
          for (const v of value) {
            if (!bucket.includes(v)) bucket.push(v);
          }
        } else {
          tools.push({ [key]: value });
        }
      }
    }
    return tools;
  }

  /**
   * Handle connector selection from connectors menu
   */
  async onConnectorsOptionSelect(option: CustomMenuItem): Promise<void> {


    // Handle "Manage Connectors" button
    if (option.value === 'manage-connectors') {
      this.router.navigate(['/settings/integrations']);
      return;
    }

    // Handle connector toggle
    const toolKitValue = option.value;

    if (option.toggleState) {
      // Enable connector
      this.integrationsService.enableConnector(toolKitValue).subscribe((res: any) => {
        if (res.data) {
          this.toastService.success('Connection enabled successfully');
        } else {
          this.toastService.error('Connection enable failed');
        }
      });

      // Update local saved state for MY_BROWSER so the effect stays in sync
      if (toolKitValue === MISC_CONNECTORS.MY_BROWSER) {
        this.browserConnectorSavedState.set(true);
      }

      this.selectedConnectors.update(connectors =>
        connectors.filter(connector => connector !== toolKitValue)
      );
      this.selectedConnectorsChange.emit([...this.getSelectedConnectorsOptions()]);
      return;
    } else {
      if (option.type !== MenuItemType.BUTTON) {
        // Disable connector
        this.integrationsService.disableConnector(toolKitValue).subscribe((res: any) => {
          if (res.data) {
            this.toastService.success('Connection disabled successfully');
          } else {
            this.toastService.error('Connection disable failed');
          }
        });

        // Update local saved state for MY_BROWSER so the effect stays in sync
        if (toolKitValue === MISC_CONNECTORS.MY_BROWSER) {
          this.browserConnectorSavedState.set(false);
        }
      }

      this.selectedConnectors.update(connectors => [...connectors, toolKitValue]);
      this.selectedConnectorsChange.emit([...this.getSelectedConnectorsOptions()]);
    }
    if (option.type === MenuItemType.BUTTON) {
      if (option.value == MISC_CONNECTORS.MY_BROWSER) {
        this.browserExtensionService.goToExtensionUrl();
        return;
      }
      option.buttonLoading = true;
      option.disabled = true;
      option.buttonLabel = ""
      if (([DATABASE_PROVIDERS.MONGODB, DATABASE_PROVIDERS.MYSQL, DATABASE_PROVIDERS.SQLITE, DATABASE_PROVIDERS.POSTGRES] as string[]).includes(toolKitValue)) {
        this.router.navigate(['/settings/integrations']);
        return;
      }
      this.integrationsService.addConnector({ toolKit: toolKitValue, callbackUrl: `bob/callback` }).subscribe((res) => {
        if (res.data.status) {
          this.toastService.success('Connection initiated successfully');
          this.connectorsMenu?.hide();
          if (res.data.redirectUrl) {
            const threadId = this.threadId();
            const threadMetadataId = this.threadMetadataId();
            if (threadId && threadMetadataId) {
              sessionStorage.setItem('connectorReturnThreadId', threadId);
              sessionStorage.setItem('connectorReturnThreadMetadataId', threadMetadataId);
            }
            window.location.href = res.data.redirectUrl;
          }
        } else {
          this.toastService.error('Connection failed', 'Connection failed');
          option.buttonLoading = false;
        }
      });
    }
  }

  /**
   * Get selected connectors as array
   */
  getSelectedConnectorsOptions(): CustomMenuItem[] {
    return this.selectedConnectors().map(id => ({
      value: id,
      label: id,
      icon: '',
      iconSource: IconSourceEnum.SVG
    }));
  }

  getSelectedConnectors(): TOOLKIT_PROVIDERS[] {
    return this.connectorsMenuItems()
      .filter(item => item.toggleState === true && item.type === MenuItemType.TOGGLE)
      .map(item => item.value as TOOLKIT_PROVIDERS);
  }

  /**
   * Remove a selected tool option
   */
  removeToolOption(option: CustomMenuItem): void {
    let mapKey: any;
    if (option.type === MenuItemType.SINGLE_SELECT && option.selectionTree) {
      mapKey = Object.keys(option.selectionTree)[0];
    } else {
      mapKey = option.value;
    }

    this.selectedToolsMap.delete(mapKey);

    // Update toggle state if applicable
    if (option.type === MenuItemType.TOGGLE) {
      this.toolsMenuItems.update(items =>
        items.map(item =>
          item.value === option.value
            ? { ...item, toggleState: false }
            : item
        )
      );
    }

    this.selectedToolsOptionsChange.emit(this.getSelectedToolsOptions());
  }

  // ============================================
  // File Selection
  // ============================================
  /**
   * Initialize file selection dialog
   */
  initializeFileSelection(optionValue: QueryOptions): void {
    this.loadKnowledgeBasesForDropdown();
    this.loadProjectsForDropdown();

    this.fileSelectionConfig.update(config => ({
      ...config,
      activeTabId: optionValue,
      attachmentsLength: this.attachments().length,
      totalUploadedAttachments: this.uploadedAttachments().length,
      initialSelectedFiles: this.attachmentFileItems()
    }));
  }

  /**
   * Load knowledge bases for dropdown.
   * When documentTypeId is set (compare context), fetches knowledge mappings for that
   * document type and filters KBs to only those mapped to it.
   * Otherwise falls back to showing all workspace/project KBs.
   */
  loadKnowledgeBasesForDropdown(): void {
    const projectId = this.projectId();
    const compareId = this.compareId();
    const documentTypeId = this.documentTypeId();

    const workspaceKbs$ = this.knowledgeBaseService.getKnowledgeBaseStore({ scope: KNOWLEDGE_SCOPE.WORKSPACE });

    // Derive unique project IDs from compare documents or fall back to projectId
    let uniqueProjectIds: string[] = [];
    if (compareId && this.compareDocuments().length > 0) {
      uniqueProjectIds = [...new Set(this.compareDocuments().map((d: any) => {
        if (!d.project) return null;
        if (typeof d.project === 'object') return d.project._id?.toString();
        return d.project.toString();
      }).filter(Boolean))] as string[];
    } else if (projectId) {
      uniqueProjectIds = [projectId];
    }

    let projectKbs$: Observable<any>;
    if (uniqueProjectIds.length > 0) {
      const observables = uniqueProjectIds.map(id =>
        this.knowledgeBaseService.getKnowledgeBaseStore({ scope: KNOWLEDGE_SCOPE.PROJECT, project: id })
      );
      projectKbs$ = forkJoin(observables).pipe(
        map(results => {
          const allStores = results.reduce((acc: any[], res: any) => {
            if (res.success && res.data?.stores) acc.push(...res.data.stores);
            return acc;
          }, []);
          return { success: true, data: { stores: allStores } };
        })
      );
    } else {
      projectKbs$ = of({ success: true, data: { stores: [] } } as any);
    }

    if (documentTypeId) {
      // Fetch knowledge mappings for this document type (workspace + each project) so we
      // can restrict the KB dropdown to only those linked to this document type.
      const workspaceMappings$ = this.knowledgeMappingService.getKnowledgeMappingsByDocumentType({
        documentTypeId,
        scope: KNOWLEDGE_SCOPE.WORKSPACE
      });

      const projectMappingObservables = uniqueProjectIds.length > 0
        ? uniqueProjectIds.map(pid => this.knowledgeMappingService.getKnowledgeMappingsByDocumentType({
          documentTypeId,
          scope: KNOWLEDGE_SCOPE.PROJECT,
          project: pid
        }))
        : [of({ success: true, data: [] } as any)];

      const allProjectMappings$ = forkJoin(projectMappingObservables).pipe(
        map(results => {
          const allMappings = results.reduce((acc: IKnowledgeMapping[], res: any) => {
            if (res.success && res.data) acc.push(...res.data);
            return acc;
          }, []);
          return { success: true, data: allMappings };
        })
      );

      forkJoin([workspaceKbs$, projectKbs$, workspaceMappings$, allProjectMappings$]).subscribe(
        ([workspaceRes, projectRes, workspaceMappingsRes, projectMappingsRes]: any[]) => {
          const mappedKbIds = new Set<string>();
          [workspaceMappingsRes, projectMappingsRes].forEach((res: any) => {
            if (res.success && res.data) {
              (res.data as IKnowledgeMapping[]).forEach(m => {
                if (m.linkedDocumentType === LINKED_DOCUMENT_TYPE.KNOWLEDGE_BASE_STORE) {
                  const id = typeof m.linkedDocument === 'string'
                    ? m.linkedDocument
                    : (m.linkedDocument as any)?._id;
                  if (id) mappedKbIds.add(id.toString());
                }
              });
            }
          });

          const filterByMappings = (stores: any[]) =>
            mappedKbIds.size > 0 ? stores.filter(kb => mappedKbIds.has(kb._id?.toString())) : [];

          this.knowledgeBases = workspaceRes.success ? filterByMappings(workspaceRes.data?.stores || []) : [];
          this.projectKnowledgeBases = projectRes.success ? filterByMappings(projectRes.data?.stores || []) : [];
          this.updateKbDropdownAndToolMenu();
        }
      );
    } else {
      forkJoin([workspaceKbs$, projectKbs$]).subscribe(([workspaceRes, projectRes]) => {
        if (workspaceRes.success) this.knowledgeBases = workspaceRes.data.stores || [];
        if (projectRes.success) this.projectKnowledgeBases = projectRes.data.stores || [];
        this.updateKbDropdownAndToolMenu();
      });
    }
  }

  private updateKbDropdownAndToolMenu(): void {
    this.fileSelectionConfig.update(config => ({
      ...config,
      tabs: config.tabs?.map(tab => {
        if (tab.id === QueryOptions.WORKSPACE_KNOWLEDGE) {
          return {
            ...tab,
            dropdownOptions: this.knowledgeBases.map(kb => ({
              id: kb._id,
              label: kb.name,
              value: kb._id,
              metadata: kb
            }))
          };
        }
        return tab;
      })
    }));

    const kbTool = this.toolsMenuItems().find(item => item.value === QueryOptions.KNOWLEDGE_BASE);
    if (kbTool) {
      const hasAny = this.knowledgeBases.length > 0 || this.projectKnowledgeBases.length > 0;
      if (!hasAny) {
        kbTool.description = 'Create a knowledge base to use this tool';
        kbTool.disabled = true;
        kbTool.subOptions = [];
      } else {
        kbTool.description = undefined;
        kbTool.disabled = false;
        const subOptions: CustomMenuItem[] = [];

        if (this.knowledgeBases.length > 0) {
          subOptions.push({ label: 'Workspace knowledge', isGroupHeader: true });
          this.knowledgeBases.forEach(kb => subOptions.push({
            label: kb.name, value: kb._id,
            icon: this.SVG_ICONS.KNOWLEDGE_BASE, iconSource: IconSourceEnum.SVG
          }));
        }

        if (this.projectKnowledgeBases.length > 0) {
          subOptions.push({ label: 'Project knowledge', isGroupHeader: true });
          this.projectKnowledgeBases.forEach(kb => subOptions.push({
            label: kb.name, value: kb._id,
            icon: this.SVG_ICONS.KNOWLEDGE_BASE, iconSource: IconSourceEnum.SVG
          }));
        }

        kbTool.subOptions = subOptions;
      }
      this.toolsMenuItems.update(items => [...items]);
    }
  }

  /**
   * Load projects for dropdown
   */
  loadProjectsForDropdown(): void {
    this.projectService.getProjects(0, 1000).subscribe((res) => {
      if (res.success) {
        this.projects = res.data.projects || [];

        // Update file selection config with project options
        this.fileSelectionConfig.update(config => ({
          ...config,
          tabs: config.tabs?.map(tab => {
            if (tab.id === QueryOptions.PROJECT_FILES || tab.id === QueryOptions.PROJECT_KNOWLEDGE) {
              return {
                ...tab,
                dropdownOptions: this.projects.map(project => ({
                  id: project._id,
                  label: project.name,
                  value: project._id,
                  metadata: project
                }))
              };
            }
            return tab;
          })
        }));

        // Also update the tools menu to show projects tool
        const projectsTool = this.toolsMenuItems().find(item => item.value === QueryOptions.PROJECTS);
        if (projectsTool) {
          this.configureMenuItem(
            this.projects,
            projectsTool,
            'Create a project to use this tool',
            this.SVG_ICONS.PROJECT
          );
        }
      }
    });
  }

  /**
   * Load linked documents for document chat context
   */
  loadLinkedDocs(): void {
    if (!this.isFeatureEnabled(THREAD_AGENT_FEATURES.LINKED_DOCS)) return;
    const docId = this.documentId();
    if (!docId) return;
    this.linkDocumentService.fetchLinkDocument(docId).subscribe(res => {
      if (res.success) {
        const linkedDocs = res.data.data;
        const linkedDocsTool = this.toolsMenuItems().find(item => item.value === QueryOptions.LINKED_DOCS);
        if (!linkedDocsTool) return;

        if (linkedDocs.length === 0) {
          linkedDocsTool.description = 'No linked documents found';
          linkedDocsTool.disabled = true;
          linkedDocsTool.subOptions = [];
        } else {
          linkedDocsTool.description = undefined;
          linkedDocsTool.disabled = false;
          linkedDocsTool.subOptions = linkedDocs.map(doc => ({
            label: doc.targetDocument.name,
            value: { _id: doc.targetDocument._id, name: doc.targetDocument.name },
            icon: this.SVG_ICONS.ATTACHMENT,
            iconSource: IconSourceEnum.SVG,
          }));
        }
        this.toolsMenuItems.update(items => [...items]);
      }
    });
  }

  /**
   * Populate Compare Documents tool sub-options for compare chat context.
   * Guards against redundant updates to avoid resetting open menu navigation.
   */
  loadCompareDocsTool(): void {
    if (!this.isFeatureEnabled(THREAD_AGENT_FEATURES.COMPARE_DOCS)) return;
    const docs = this.compareDocuments();
    const prev = this.COMPARE_DOCS_TOOL.subOptions ?? [];
    if (prev.length === docs.length && docs.every((d, i) => prev[i]?.value === d._id)) return;
    this.configureMenuItem(docs, this.COMPARE_DOCS_TOOL, 'No compare documents available', this.SVG_ICONS.ATTACHMENT);
    this.toolsMenuItems.update(items => [...items]);
  }

  /**
   * Populate KPIs tool sub-options for compare chat context.
   * Guards against redundant updates to avoid resetting open menu navigation.
   */
  loadKpisTool(): void {
    if (!this.isFeatureEnabled(THREAD_AGENT_FEATURES.KPIS)) return;
    const kpis = this.kpiDocuments();
    const prev = this.KPIS_TOOL.subOptions ?? [];
    if (prev.length === kpis.length && kpis.every((k, i) => prev[i]?.value === k._id)) return;
    this.configureMenuItem(kpis, this.KPIS_TOOL, 'No KPIs available', this.SVG_ICONS.KNOWLEDGE_BASE);
    this.toolsMenuItems.update(items => [...items]);
  }

  /**
   * Populate Form Questions tool sub-options, grouped by section title when available.
   */
  loadFormQuestionsTool(): void {
    if (!this.isFeatureEnabled(THREAD_AGENT_FEATURES.FORM_QUESTIONS)) return;
    const questions = this.formQuestions();
    const prev = this.FORM_QUESTIONS_TOOL.subOptions ?? [];

    const hasSameItems = prev.filter(p => !p.isGroupHeader).length === questions.length
      && questions.every(q => prev.some(p => !p.isGroupHeader && p.value === q._id));
    if (hasSameItems && prev.length > 0) return;

    if (questions.length === 0) {
      this.FORM_QUESTIONS_TOOL.description = 'No form questions available';
      this.FORM_QUESTIONS_TOOL.disabled = true;
      this.FORM_QUESTIONS_TOOL.subOptions = [];
      this.toolsMenuItems.update(items => [...items]);
      return;
    }

    this.FORM_QUESTIONS_TOOL.description = undefined;
    this.FORM_QUESTIONS_TOOL.disabled = false;

    const hasSections = questions.some(q => q.sectionTitle);
    if (hasSections) {
      const subOptions: CustomMenuItem[] = [];
      let currentSection: string | undefined;
      for (const q of questions) {
        if (q.sectionTitle && q.sectionTitle !== currentSection) {
          currentSection = q.sectionTitle;
          subOptions.push({ label: q.sectionTitle, isGroupHeader: true });
        }
        subOptions.push({ label: q.name, value: q._id, icon: 'circle-help', iconSource: IconSourceEnum.LUCIDE });
      }
      this.FORM_QUESTIONS_TOOL.subOptions = subOptions;
    } else {
      this.configureMenuItem(questions, this.FORM_QUESTIONS_TOOL, 'No form questions available', 'help-circle');
    }

    this.toolsMenuItems.update(items => [...items]);
  }

  /**
   * Configure menu item with dynamic sub options
   */
  private configureMenuItem(items: any[], menuItem?: CustomMenuItem, emptyStateDescription?: string, icon?: string): void {
    if (!menuItem) return;

    if (items.length === 0) {
      menuItem.description = emptyStateDescription;
      menuItem.disabled = true;
      menuItem.subOptions = [];
      return;
    }

    menuItem.description = undefined;
    menuItem.disabled = false;
    menuItem.subOptions = items.map(item => ({
      label: item.name,
      value: item._id,
      icon,
      iconSource: IconSourceEnum.SVG,
    }));
  }

  /**
   * Handle file selection dialog cancel
   */
  onFileSelectionCancel(): void {
    this.openFileSelectionDialog.set(false);
  }


  private resolveUploadDocModel(tabId?: string): DOCUMENT_MODEL {
    switch (tabId) {
      case QueryOptions.PROJECT_FILES:
        return DOCUMENT_MODEL.CONTRACT;
      case QueryOptions.KNOWLEDGE_BASE:
      case QueryOptions.WORKSPACE_KNOWLEDGE:
      case QueryOptions.PROJECT_KNOWLEDGE:
        return DOCUMENT_MODEL.REPOSITORY;
      default:
        return DOCUMENT_MODEL.ATTACHMENT;
    }
  }

  /**
   * Handle file selection dialog submit
   */
  onFileSelectionSubmit(event: FileSelectionSubmitEvent): void {
    // Prevent file upload when disabled
    if (this.disabled()) {
      return;
    }

    this.filesInProgress.set(true);
    const formData = new FormData();

    const stagedIds = new Set(this.stagedFilesMap.keys());

    // Handle removals: deselected files that were previously attached
    const selectedIds = new Set(event.allSelectedFiles?.map(f => f.id) || []);
    const removedFiles = this.attachmentFileItems().filter(item => !selectedIds.has(item.id) && !stagedIds.has(item.id));
    if (removedFiles.length > 0) {
      removedFiles.forEach(file => {
        this.attachments.update(a => a.filter(att => att._id !== file.id));
        this.uploadedAttachments.update(uploaded => uploaded.filter(a => a._id !== file.id));
        if (file.metadata?.originalAttachment?.docModel === DOCUMENT_MODEL.ATTACHMENT) {
          this.deleteUploadedFileMutation.mutate({ fileId: file.id, threadMetadataId: this.threadMetadataId() });
        }
      });
      this.updateUploadingStatus();
    }

    const batchTempIdsList: string[] = [];

    if (this.stagedFilesMap.size > 0) {
      const stagedEntries = Array.from(this.stagedFilesMap.entries());
      this.stagedFilesMap.clear();

      stagedEntries.forEach(([tempId, file]) => {
        formData.append('fileUploads', file, file.name);
        batchTempIdsList.push(tempId);
        this.attachments.update(current => current.map(a =>
          a._id === tempId ? { ...a, status: EMBEDDINGS_GENERATION_STATUS.IN_PROGRESS } : a
        ));
      });
      this.updateUploadingStatus();
    }

    // Handle uploaded files
    if (event.uploadedFiles && event.uploadedFiles.length > 0) {
      const FILE_SIZE_LIMIT = 5 * 1024 * 1024; // 5MB
      if (event.uploadedFiles.some((file: File) => file.size > FILE_SIZE_LIMIT)) {
        this.largeFileAttached.set(true);
      }

      const uploadDocModel = this.resolveUploadDocModel(event.activeTabId);
      const tempAttachments: ChatAttachment[] = event.uploadedFiles.map((file: File, index: number) => ({
        _id: `temp-${Date.now()}-${index}`,
        name: file.name,
        type: getFileType(file.name) || FILE_TYPE.PDF,
        docModel: uploadDocModel,
        status: EMBEDDINGS_GENERATION_STATUS.IN_PROGRESS
      }));

      // Add to attachments immediately so they show in UI
      this.attachments.update(current => [...current, ...tempAttachments]);
      this.updateUploadingStatus();

      for (const file of event.uploadedFiles) {
        formData.append('fileUploads', file, file.name);
      }

      formData.append('uploadDocModel', uploadDocModel);

      tempAttachments.forEach((attachment) => {
        batchTempIdsList.push(attachment._id);
      });
    }

    // Handle selected/linked files (skip ones already marked as attachments)
    const newSelectedFiles = event.allSelectedFiles?.filter(file => !file.metadata?.isAttachment) || [];

    newSelectedFiles.forEach((file: FileItem) => {
      const metadata = file.metadata;

      if (metadata.tabId === QueryOptions.WORKSPACE_KNOWLEDGE) {
        formData.append('linkedFiles', JSON.stringify({
          _id: metadata.originalFile._id,
          name: file.name,
          docModel: DOCUMENT_MODEL.REPOSITORY,
          metadata: {
            tabId: QueryOptions.WORKSPACE_KNOWLEDGE,
            dropdownId: metadata.kbId || 'file-repository',
            kbId: metadata.kbId,
            kbName: metadata.kbName,
          },
        }));
      } else if (metadata.tabId === QueryOptions.PROJECT_KNOWLEDGE) {
        if (!metadata.originalFile?._id) {
          console.warn('[FileSelection] Skipping project knowledge file with missing originalFile:', file.name);
          return;
        }
        formData.append('linkedFiles', JSON.stringify({
          _id: metadata.originalFile._id,
          name: file.name,
          docModel: DOCUMENT_MODEL.REPOSITORY,
          metadata: {
            tabId: QueryOptions.PROJECT_KNOWLEDGE,
            dropdownId: metadata.projectId || '',
            projectId: metadata.projectId,
            projectName: metadata.projectName,
            isProjectKnowledge: true,
          },
        }));
      } else if (metadata.tabId === QueryOptions.PROJECT_FILES) {
        formData.append('linkedFiles', JSON.stringify({
          _id: file.id,
          name: file.name,
          docModel: DOCUMENT_MODEL.CONTRACT,
          metadata: {
            tabId: QueryOptions.PROJECT_FILES,
            dropdownId: metadata.projectId || '',
            projectId: metadata.projectId,
            projectName: metadata.projectName,
          },
        }));
      }
    });

    // Include threadData for pre-upload to sandbox (existing chats only)
    if (this.threadMetadataId()) {
      formData.append('threadData', JSON.stringify({
        threadId: this.threadId(),
        threadMetadataId: this.threadMetadataId(),
        provider: this.provider()
      }));
    }

    if (formData.has('fileUploads') || formData.has('linkedFiles')) {
      if (batchTempIdsList.length > 0) {
        formData.append('_tempIds', JSON.stringify(batchTempIdsList));
      }
      this.attachFilesMutation.mutate(formData);
    } else {
      // Only removals happened — close dialog, stop progress
      this.filesInProgress.set(false);
      this.openFileSelectionDialog.set(false);
    }
  }

  // ============================================
  // Setup Methods
  // ============================================

  private buildOptionsMenuItems(): void {
    const documentsAccess = this.features.checkFeature(UI_FEATURE.DOCUMENTS);
    const projectsAccess = this.features.checkFeature(UI_FEATURE.PROJECTS);
    const hasAnyInnDocFeatures = documentsAccess.allowed || projectsAccess.allowed;

    const items: CustomMenuItem[] = [];
    if (hasAnyInnDocFeatures) {
      items.push({
        label: 'Repository files',
        value: QueryOptions.KNOWLEDGE_BASE,
        icon: this.SVG_ICONS.KNOWLEDGE_BASE,
        iconSource: IconSourceEnum.SVG,
      });
    }
    if (documentsAccess.allowed) {
      items.push({
        label: 'Document files',
        value: QueryOptions.PROJECT_FILES,
        icon: this.SVG_ICONS.PROJECT,
        iconSource: IconSourceEnum.SVG,
      });
    }
    items.push({
      label: 'File from local',
      value: QueryOptions.ADD_PHOTOS_AND_FILES,
      icon: this.SVG_ICONS.ATTACHMENT,
      iconSource: IconSourceEnum.SVG,
      subOptions: [],
    });
    this.optionsMenuItems.set(items);
  }

  /**
   * Add premium tools based on feature flags
   */
  private addPremiumTools(): void {
    const tools = this.toolsMenuItems();

    if (this.features.hasSpecificAgentFeature(AGENT_FEATURES.IMAGE_GENERATION)) {
      tools.unshift(this.IMAGE_GENERATION_TOOL);
    }
    if (this.features.hasSpecificAgentFeature(AGENT_FEATURES.KNOWLEDGE_BASE_TOOL)) {
      tools.unshift(this.KNOWLEDGE_BASE_TOOL);
    }
    if (this.features.hasSpecificAgentFeature(AGENT_FEATURES.PROJECT_TOOL)) {
      tools.unshift(this.PROJECTS_TOOL);
    }
    if (this.isFeatureEnabled(THREAD_AGENT_FEATURES.LINKED_DOCS)) {
      tools.unshift(this.LINKED_DOCS_TOOL);
    }
    if (this.isFeatureEnabled(THREAD_AGENT_FEATURES.COMPARE_DOCS)) {
      tools.unshift(this.COMPARE_DOCS_TOOL);
    }
    if (this.isFeatureEnabled(THREAD_AGENT_FEATURES.KPIS)) {
      tools.unshift(this.KPIS_TOOL);
    }
    if (this.isFeatureEnabled(THREAD_AGENT_FEATURES.FORM_QUESTIONS)) {
      tools.unshift(this.FORM_QUESTIONS_TOOL);
    }

    this.toolsMenuItems.set(tools);
  }

  /**
   * Load connectors from API
   */
  getConnectors(): void {
    this.integrationsService.getConnectors().subscribe((res: any) => {
      if (res.success) {
        const connectorsData = res.data;
        const integrationMapping: { [key: string]: { name: string, icon: string } } = CONNECTOR_PROVIDER_INFO;
        const connectorMenuItems: CustomMenuItem[] = [];

        // Add "Manage Connectors" button at the top
        connectorMenuItems.push({
          label: 'Manage Connectors',
          value: 'manage-connectors',
          icon: 'settings-2',
          type: MenuItemType.SINGLE_SELECT,
          iconSource: IconSourceEnum.LUCIDE,
          subOptions: [],
          buttonCallback: () => {
            this.router.navigate(['/settings/integrations']);
          }
        }, {
          label: CONNECTOR_PROVIDER_INFO[MISC_CONNECTORS.MY_BROWSER].name,
          value: MISC_CONNECTORS.MY_BROWSER,
          icon: CONNECTOR_PROVIDER_INFO[MISC_CONNECTORS.MY_BROWSER].icon,
          iconSource: IconSourceEnum.SVG,
          subOptions: [],
          class: '[&>svg]:w-4 [&>svg]:h-4',
        });

        // Add connectors based on API response
        for (const category of connectorsData) {
          if (category.values && Array.isArray(category.values)) {
            for (const connector of category.values) {
              // MY_BROWSER is already in the initial connectorsMenuItems list;
              // store its raw saved state (true/false/null) so the extension-install effect can use it.
              // null = no settings doc yet (default ON), false = explicitly disabled, true = enabled
              if (connector.name === MISC_CONNECTORS.MY_BROWSER) {
                this.browserConnectorSavedState.set(connector.toolkit);
                continue;
              }

              if (integrationMapping[connector.name]) {
                const mapping = integrationMapping[connector.name];
                let menuItem: CustomMenuItem = {
                  label: mapping.name,
                  value: connector.name,
                  icon: mapping.icon,
                  iconSource: IconSourceEnum.SVG,
                  subOptions: [],
                  class: '[&>svg]:w-4 [&>svg]:h-4'
                };

                if (connector.toolkit === true) {
                  menuItem = {
                    ...menuItem,
                    type: MenuItemType.TOGGLE,
                    toggleState: true,
                  };
                } else if (connector.toolkit === false) {
                  menuItem = {
                    ...menuItem,
                    type: MenuItemType.TOGGLE,
                    toggleState: false,
                  };
                } else if (connector.toolkit === null) {
                  menuItem = {
                    ...menuItem,
                    type: MenuItemType.BUTTON,
                    buttonLabel: 'Connect',
                    buttonClass: 'theme-text border-none'
                  };
                }

                connectorMenuItems.push(menuItem);
              }
            }
          }
        }

        this.connectorsMenuItems.set(connectorMenuItems);
      }
    });
  }

  /**
   * Setup socket listeners for file embeddings status changes
   */
  setupSocketListeners(): void {
    this.embeddingsStatusSubscription = this.socketService
      .on<{ _id: string, status: EMBEDDINGS_GENERATION_STATUS }>(SOCKET_EVENTS.GENERAL_AGENT_FILE_EMBEDDINGS_STATUS_CHANGED)
      .subscribe(res => {
        if (res && res[0]) {
          const { _id, status } = res[0];



          // Update uploadedAttachments signal
          this.uploadedAttachments.update(uploadedAttachments =>
            uploadedAttachments.map(attachment =>
              attachment._id === _id ? { ...attachment, status } : attachment
            )
          );

          // Also update attachments array
          this.attachments.update(attachments =>
            attachments.map(attachment =>
              attachment._id === _id ? { ...attachment, status } : attachment
            )
          );

          // Update button state
          this.updateUploadingStatus();

          // Check if all embeddings are completed
          if (this.uploadedAttachments().every(uploadedAttachment => uploadedAttachment.status === EMBEDDINGS_GENERATION_STATUS.COMPLETED)) {
            this.filesInProgress.set(false);
          }
          if (
            this.deferredSendPayload &&
            this.uploadedAttachments().every(a => a.status !== EMBEDDINGS_GENERATION_STATUS.IN_PROGRESS)
          ) {
            const payload = this.deferredSendPayload;
            this.deferredSendPayload = null;
            this.sendMessageEvent.emit(payload);
            this.clearInput();
          }
        }
      });
  }

  // ============================================
  // Attachment Helpers
  // ============================================
  /**
   * Handle paste event - uploads clipboard files (Ctrl+V), or auto-uploads large text as file
   */
  onPaste(event: ClipboardEvent): void {
    // Prevent file upload via paste when disabled
    if (this.disabled()) {
      return;
    }

    // Files pasted from clipboard (e.g. screenshot, copied file from file manager)
    const clipboardFiles = event.clipboardData?.files;
    if (clipboardFiles && clipboardFiles.length > 0) {
      event.preventDefault();
      if (!this.threadId() && !this.threadMetadataId()) {
        this.handleNewChatLocalFiles(Array.from(clipboardFiles));
      } else {
        this.uploadLocalFiles(Array.from(clipboardFiles));
      }
      return;
    }

    // Large text paste → auto-upload as .txt (original behaviour, unchanged)
    const pastedText = event.clipboardData?.getData('text/plain');
    if (pastedText && pastedText.length > 10000) {
      event.preventDefault();
      const blob = new Blob([pastedText], { type: 'text/plain' });

      const now = new Date();
      let hours = now.getHours();
      const minutes = now.getMinutes();
      const amOrPm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const timeStr = `${hours}:${String(minutes).padStart(2, '0')}${amOrPm}`;
      const fileName = `pasted-content-${timeStr}.txt`;
      const file = new File([blob], fileName, { type: 'text/plain' });

      if (!this.threadId() && !this.threadMetadataId()) {
        this.handleNewChatLocalFiles([file]);
        return;
      }

      this.filesInProgress.set(true);
      const formData = new FormData();
      formData.append('fileUploads', file, file.name);
      formData.append('_tempIds', '[]');

      formData.append('uploadDocModel', DOCUMENT_MODEL.ATTACHMENT);

      if (this.threadMetadataId()) {
        formData.append('threadData', JSON.stringify({
          threadId: this.threadId(),
          threadMetadataId: this.threadMetadataId(),
          provider: this.provider()
        }));
      }

      this.attachFilesMutation.mutate(formData);
    }
  }

  /**
   * Upload local File objects via the same flow as the file selection dialog.
   * Used by drag-drop and clipboard paste.
   */
  private uploadLocalFiles(files: File[]): void {
    const FILE_SIZE_LIMIT = 5 * 1024 * 1024;
    if (files.some(f => f.size > FILE_SIZE_LIMIT)) {
      this.largeFileAttached.set(true);
    }

    const tempAttachments: ChatAttachment[] = files.map((file, index) => ({
      _id: `temp-${Date.now()}-${index}`,
      name: file.name,
      type: getFileType(file.name) || FILE_TYPE.PDF,
      docModel: DOCUMENT_MODEL.ATTACHMENT,
      status: EMBEDDINGS_GENERATION_STATUS.IN_PROGRESS
    }));

    this.attachments.update(current => [...current, ...tempAttachments]);
    this.updateUploadingStatus();
    this.filesInProgress.set(true);

    const formData = new FormData();
    files.forEach(file => formData.append('fileUploads', file, file.name));

    formData.append('uploadDocModel', DOCUMENT_MODEL.ATTACHMENT);

    if (this.threadMetadataId()) {
      formData.append('threadData', JSON.stringify({
        threadId: this.threadId(),
        threadMetadataId: this.threadMetadataId(),
        provider: this.provider()
      }));
    }

    formData.append('_tempIds', JSON.stringify(tempAttachments.map(a => a._id)));
    this.attachFilesMutation.mutate(formData);
  }


  private handleNewChatLocalFiles(files: File[]): void {
    if (this.creatingThreadFromUpload) {
      this.stageLocalFiles(files);
      return;
    }
    this.creatingThreadFromUpload = true;
    this.uploadLocalFiles(files);
  }

  private stageLocalFiles(files: File[]): void {
    const FILE_SIZE_LIMIT = 5 * 1024 * 1024;
    if (files.some(f => f.size > FILE_SIZE_LIMIT)) {
      this.largeFileAttached.set(true);
    }

    const tempAttachments: ChatAttachment[] = files.map((file, index) => {
      const tempId = `temp-staged-${Date.now()}-${index}`;
      this.stagedFilesMap.set(tempId, file);
      return {
        _id: tempId,
        name: file.name,
        type: getFileType(file.name) || FILE_TYPE.PDF,
        docModel: DOCUMENT_MODEL.ATTACHMENT,
      };
    });

    this.attachments.update(current => [...current, ...tempAttachments]);
  }

  private persistStagedFilesOnDestroy(): void {
    if (this.stagedFilesMap.size === 0) return;

    const filesToUpload = Array.from(this.stagedFilesMap.values());
    this.stagedFilesMap.clear();

    this.uploadLocalFiles(filesToUpload);
  }

  /**
   * Update uploading files status
   */
  private updateUploadingStatus(): void {
    const hasUploading = this.attachments().some(attachment =>
      attachment.status === EMBEDDINGS_GENERATION_STATUS.IN_PROGRESS
    );
    this.hasUploadingFiles.set(hasUploading);
  }

  /**
   * Remove attached file
   */
  removeAttachedFile(file: { _id: string, name: string, docModel: DOCUMENT_MODEL }): void {
    const matchingFileItem = this.attachmentFileItems().find(item => item.id === file._id);

    // Staged files exist only locally — remove from map without calling the delete API
    if (this.stagedFilesMap.has(file._id)) {
      this.stagedFilesMap.delete(file._id);
      this.attachments.update(attachments => attachments.filter(a => a._id !== file._id));
      if (matchingFileItem) this.fileSelectionDialogRef?.removeSelectedFile(matchingFileItem);
      return;
    }

    this.attachments.update(attachments => attachments.filter(a => a._id !== file._id));
    if (matchingFileItem) this.fileSelectionDialogRef?.removeSelectedFile(matchingFileItem);
    this.updateUploadingStatus();

    this.uploadedAttachments.update(uploadedAttachments =>
      uploadedAttachments.filter(uploadedAttachment => uploadedAttachment._id !== file._id)
    );

    if (file.docModel === DOCUMENT_MODEL.ATTACHMENT) {
      this.deleteUploadedFileMutation.mutate({ fileId: file._id, threadMetadataId: this.threadMetadataId() });
    }
  }

  private chatAttachmentToFileItem(att: ChatAttachment): FileItem {
    const sc = att.metadata;
    if (sc?.['tabId']) {
      return {
        id: att._id,
        name: att.name,
        metadata: {
          tabId: sc['tabId'],
          dropdownId: sc['dropdownId'] || '',
          kbId: sc['kbId'],
          kbName: sc['kbName'],
          projectId: sc['projectId'],
          projectName: sc['projectName'],
          isProjectKnowledge: sc['isProjectKnowledge'] ?? false,
          originalAttachment: att.attachmentData,
          isAttachment: true,
        }
      };
    }
    let tabId: string;
    let dropdownId = '';
    if (att.docModel === DOCUMENT_MODEL.ATTACHMENT) {
      tabId = QueryOptions.ADD_PHOTOS_AND_FILES;
      dropdownId = 'Uploaded';
    } else if (att.docModel === DOCUMENT_MODEL.CONTRACT) {
      tabId = QueryOptions.PROJECT_FILES;
    } else {
      tabId = QueryOptions.WORKSPACE_KNOWLEDGE;
    }
    return {
      id: att._id,
      name: att.name,
      metadata: {
        tabId,
        dropdownId,
        originalAttachment: att.attachmentData,
        isAttachment: true,
      }
    };
  }
}
